import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { Collapse, Modal, Popover } from "bootstrap";
import "./style.css";

import {
  calculatorDefaults,
  calculatorGroups,
  calculatorUsesGunField,
  findGunField,
  getCalculator,
  gunDefaults,
  gunEditor,
  gunFieldNames,
} from "./calculators.js";
import {
  encodeCgiParameters,
  executeCgi,
  parseCgiResponse,
} from "./cgi.js";
import {
  extractEngagementZoneResult,
} from "./engagement-zone.js";
import {
  engagementZonePlotGeometry,
  engagementZoneScoringOverlayGeometry,
  engagementZoneSvgPoint,
} from "./engagement-zone-view.js";
import {
  applyTheme,
  applyUnitProfile,
  clearUnitOverrides,
  clearUnitOverridesForPrefix,
  loadSettings,
  loadUnitOverrides,
  setUnitOverride,
} from "./settings.js";
import {
  convertUnitValue,
  fieldDisplayConstraints,
  fieldValueFromCanonical,
  fieldValueToCanonical,
  formatUnitValue,
  getCorrelatedResultUnit,
  getFieldUnit,
  getFieldUnitCorrelation,
  getFieldUnitGroup,
  getFieldUnitOptions,
  getMvTemperatureTableUnitOptions,
  getMvTemperatureTableUnits,
  getResultUnit,
  getResultUnitCorrelation,
  getResultUnitGroup,
  getResultUnitOptions,
  getUnitLabel,
  mvTemperatureRowsFromCanonical,
  mvTemperatureRowsToCanonical,
  velocityBcFieldValueToCanonical,
} from "./units.js";
import {
  buildTrajectoryChartGeometry,
  chartXPosition,
  decimateTrajectoryChartPoints,
  extractTrajectoryIndicators,
  extractTrajectorySeries,
  formatChartTick,
  trajectoryChartScalePoints,
} from "./trajectory-chart.js";
import {
  calculateRangeTable,
  parseTemperatureList,
  rangeTableMarkersAt,
  rangeTableTransonicRegionAt,
  rangeTableValueAt,
} from "./range-table.js";
import {
  GunLibraryPersistenceError,
  createLocalStorageGunLibrary,
} from "./gun-library.js";
import { zeroSafeOptionalFieldValue } from "./form-values.js";
import {
  applyMvTemperatureModel,
  mergeLoadedGunValues,
  validateLoadedGunCompatibility,
} from "./loaded-gun.js";
import {
  migrateLegacyMvTemperatureTable,
  normalizeMvTemperatureTable,
  parseMvTemperatureTable,
} from "./mv-temperature.js";
import {
  rangeTableSampledColumnVisibility,
  visibleResultColumnIndices,
} from "./result-columns.js";
import {
  calculateSolution,
  findSolutionTargetSample,
  solutionRangeIncrement,
} from "./solution.js";
import {
  cardinalWindFromComponents,
  prepareTrajectoryParameters,
} from "./wind.js";

const APP_CLEANUP_KEY = Symbol.for("allen-ballistics-classic.app.cleanup");
if (typeof window[APP_CLEANUP_KEY] === "function") window[APP_CLEANUP_KEY]();

const app = document.querySelector("#app");
const links = document.querySelector("#calculator-links");
const INPUT_PREFIX = "jbm.inputs.";
const TRAJECTORY_RANGE_MIGRATION_PREFIX = "abc.migrations.trajectory-range-3000m.";
const CARDINAL_WIND_MIGRATION_PREFIX = "abc.migrations.cardinal-wind-v1.";
const LOADED_GUN_MIGRATION_KEY = "abc.migrations.loaded-gun-v1";
const resultCache = new Map();
const resultGunProvenance = new Map();
const expandedMvTemperatureEditors = new Set();
const gunLibrary = createLocalStorageGunLibrary({
  fields: gunEditor.sections.flatMap((section) => section.fields),
  fieldNames: gunFieldNames,
  defaults: gunDefaults(),
});
migrateLegacyGunSettings();
let fieldHelpPopovers = [];
let calculatorLayoutFrame = 0;
const sectionLayoutRects = new WeakMap();
const DESKTOP_LAYOUT_QUERY = "(min-width: 992px)";
const SECTION_COLUMN_GAP = 13.6;

function storedInputRecord(slug) {
  try {
    const value = JSON.parse(localStorage.getItem(INPUT_PREFIX + slug) || "null");
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  } catch (_) {
    return {};
  }
}

function legacyGunDraft() {
  const saved = {};
  for (const slug of [
    "maximum-distance", "point-blank-range", "range-table", "trajectory-simplified",
    "trajectory",
  ]) {
    Object.assign(saved, storedInputRecord(slug));
  }
  if (saved.bcm === undefined) {
    const legacyBandsEnabled = saved.bce === true || saved.bce === "true"
      || saved.bce === "on";
    if (Object.prototype.hasOwnProperty.call(saved, "bcp")) {
      saved.bcm = legacyBandsEnabled ? "3" : "1";
    } else if (String(saved.bcb || "").trim()) saved.bcm = "3";
  }
  if (saved.mvtb === undefined && saved.mv !== undefined) {
    saved.mvtb = migrateLegacyMvTemperatureTable(saved.mv, saved.mvt ?? 59);
  }
  return Object.fromEntries(gunFieldNames.flatMap((name) => (
    Object.prototype.hasOwnProperty.call(saved, name) ? [[name, saved[name]]] : []
  )));
}

function migrateLegacyGunSettings() {
  try {
    if (localStorage.getItem(LOADED_GUN_MIGRATION_KEY)
      || localStorage.getItem(gunLibrary.storageKey)) return false;
    const migrated = legacyGunDraft();
    if (Object.keys(migrated).length === 0) return false;
    gunLibrary.setDraft({ ...gunDefaults(), ...migrated }, { configured: false });
    localStorage.setItem(LOADED_GUN_MIGRATION_KEY, "1");
    return true;
  } catch (_) {
    return false;
  }
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function readSavedValues(calculator) {
  const defaults = calculatorDefaults(calculator);
  try {
    const currentSaved = JSON.parse(
      localStorage.getItem(INPUT_PREFIX + calculator.slug) || "{}",
    );
    const legacySaved = calculator.slug === "trajectory"
      ? JSON.parse(localStorage.getItem(`${INPUT_PREFIX}trajectory-simplified`) || "{}")
      : {};
    const saved = { ...legacySaved, ...currentSaved };
    if (calculator.slug === "trajectory"
      && saved.mvtb === undefined && saved.mv !== undefined) {
      saved.mvtb = migrateLegacyMvTemperatureTable(saved.mv, saved.mvt ?? 59);
      delete saved.mv;
      delete saved.mvt;
      localStorage.setItem(INPUT_PREFIX + calculator.slug, JSON.stringify(saved));
    }
    if (saved.outputMode === undefined && saved.un !== undefined
      && ["trajectory", "solution", "range-table"].includes(calculator.slug)) {
      saved.outputMode = String(saved.un) === "1" ? "linear" : "angular";
    }
    if (saved.bcm === undefined) {
      const legacyModeWasStored = Object.prototype.hasOwnProperty.call(saved, "bcp");
      const legacyEnabled = saved.bce === true || saved.bce === "true" || saved.bce === "on";
      if (legacyModeWasStored) saved.bcm = legacyEnabled ? "3" : "1";
      else if (String(saved.bcb || "").trim()) saved.bcm = "3";
    }
    const windMigrationKey = CARDINAL_WIND_MIGRATION_PREFIX + calculator.slug;
    if (["trajectory", "range-table"].includes(calculator.slug)
      && !localStorage.getItem(windMigrationKey)) {
      if (saved.ws === undefined && saved.wd === undefined
        && (saved.wx !== undefined || saved.wz !== undefined)) {
        const migratedWind = cardinalWindFromComponents(
          saved.wx ?? 0, saved.wz ?? 0, saved.faz ?? 0,
        );
        saved.ws = String(migratedWind.windSpeedMph);
        saved.wd = String(migratedWind.windFromDegrees);
        delete saved.wx;
        delete saved.wz;
        localStorage.setItem(INPUT_PREFIX + calculator.slug, JSON.stringify(saved));
      }
      localStorage.setItem(windMigrationKey, "1");
    }
    const migrationKey = TRAJECTORY_RANGE_MIGRATION_PREFIX + calculator.slug;
    if (calculator.module === "trajectory" && !localStorage.getItem(migrationKey)) {
      if (saved.rx === "1000") {
        saved.rx = defaults.rx;
        localStorage.setItem(INPUT_PREFIX + calculator.slug, JSON.stringify(saved));
      }
      localStorage.setItem(migrationKey, "1");
    }
    return { ...defaults, ...saved };
  } catch (_) {
    return defaults;
  }
}

function renderSidebar() {
  links.innerHTML = calculatorGroups.map((group) => `
    <section class="sidebar-group">
      <h2 class="sidebar-heading">${escapeHtml(group.title)}</h2>
      <nav aria-label="${escapeHtml(group.title)} calculators">
        ${group.calculators.map((calculator) => `
          <a class="calculator-link" data-calculator-link="${calculator.slug}"
            href="#/calculator/${calculator.slug}">
            <i class="bi ${calculator.icon}" aria-hidden="true"></i>
            <span>${escapeHtml(calculator.shortTitle)}</span>
          </a>
        `).join("")}
      </nav>
    </section>
  `).join("");
}

function renderUnitSelector(field, unitId) {
  const options = getFieldUnitOptions(field);
  if (options.length < 2) {
    return `<span class="input-group-text">${escapeHtml(getUnitLabel(unitId))}</span>`;
  }

  return `
    <button class="btn btn-outline-secondary dropdown-toggle unit-selector" type="button"
      data-bs-toggle="dropdown" data-unit-selector="${field.name}" aria-expanded="false"
      aria-label="Change unit for ${escapeHtml(field.label)}">
      <span data-unit-label>${escapeHtml(getUnitLabel(unitId))}</span>
    </button>
    <ul class="dropdown-menu dropdown-menu-end unit-menu" aria-label="Units for ${escapeHtml(field.label)}">
      ${options.map((option) => `
        <li><button class="dropdown-item unit-choice${option.id === unitId ? " active" : ""}" type="button"
          data-unit-choice="${option.id}" data-field-name="${field.name}"
          ${field.unitField ? `data-unit-field-value="${escapeHtml(option.fieldValue)}"` : ""}
          aria-pressed="${option.id === unitId}">
          <span>${escapeHtml(option.label)}</span><i class="bi bi-check2" aria-hidden="true"></i>
        </button></li>`).join("")}
    </ul>`;
}

function helpText(help, values) {
  if (typeof help === "string") return help;
  if (!help?.name || !help.values) return "";
  return help.values[String(values[help.name])] || help.default || "";
}

function renderFieldHelp(field, values) {
  const text = helpText(field.help, values);
  if (!text) return "";
  const dynamic = typeof field.help === "object" ? `
    data-help-controller="${escapeHtml(field.help.name)}"
    data-help-values="${escapeHtml(JSON.stringify(field.help.values))}"` : "";
  return `<button class="field-help-button" type="button" data-field-help="${escapeHtml(field.name)}"
    data-help-title="${escapeHtml(field.label)}" data-help-text="${escapeHtml(text)}"${dynamic}
    aria-label="Help for ${escapeHtml(field.label)}" aria-expanded="false">?</button>`;
}

function renderFieldLabel(field, id, values) {
  return `<div class="field-label-row">
    <label class="form-label" for="${id}">${escapeHtml(field.label)}</label>
    ${renderFieldHelp(field, values)}
  </div>`;
}

function mvTemperatureOverrideKey(calculator, axis) {
  return `${calculator.slug}.mvtb.${axis}`;
}

function mvTemperatureDisplayRows(canonical, temperatureUnit, velocityUnit) {
  const displayed = mvTemperatureRowsFromCanonical(
    canonical, temperatureUnit, velocityUnit,
  );
  const canonicalRows = normalizeMvTemperatureTable(
    parseMvTemperatureTable(canonical),
  );
  return displayed.map((row, index) => ({ ...row, ...canonicalRows[index] }));
}

function mvTemperatureSummary(rows, temperatureUnit) {
  const count = rows.length;
  const countLabel = `${count} measurement${count === 1 ? "" : "s"}`;
  const rawTemperatures = rows.map(({ temperature }) => String(temperature).trim());
  const temperatures = rawTemperatures.map(Number);
  if (rawTemperatures.some((temperature) => temperature === "")
    || temperatures.some((temperature) => !Number.isFinite(temperature))) {
    return `${countLabel} · incomplete`;
  }
  const minimum = Math.min(...temperatures);
  const maximum = Math.max(...temperatures);
  const span = minimum === maximum ? String(minimum) : `${minimum}–${maximum}`;
  return `${countLabel} · ${span} ${getUnitLabel(temperatureUnit)}`;
}

function renderMvTemperatureUnitSelector(field, axis, unitId) {
  const label = axis === "temperature" ? "temperature" : "muzzle velocity";
  return `
    <div class="dropdown mv-temperature-unit">
      <button class="btn btn-outline-secondary dropdown-toggle unit-selector" type="button"
        data-bs-toggle="dropdown" data-unit-selector="${field.name}.${axis}"
        aria-expanded="false" aria-label="Change unit for MV-Temp ${label}">
        <span data-unit-label>${escapeHtml(getUnitLabel(unitId))}</span>
      </button>
      <ul class="dropdown-menu dropdown-menu-end unit-menu"
        aria-label="Units for MV-Temp ${label}">
        ${getMvTemperatureTableUnitOptions(axis).map((option) => `
          <li><button class="dropdown-item unit-choice${option.id === unitId ? " active" : ""}"
            type="button" data-unit-choice="${option.id}" data-field-name="${field.name}"
            data-mv-temperature-axis="${axis}" aria-pressed="${option.id === unitId}">
            <span>${escapeHtml(option.label)}</span><i class="bi bi-check2" aria-hidden="true"></i>
          </button></li>`).join("")}
      </ul>
    </div>`;
}

function mvTemperatureRowMarkup(row, index, temperatureUnit, velocityUnit,
  removable) {
  const temperatureMin = formatUnitValue(convertUnitValue(
    -40, "fahrenheit", temperatureUnit), temperatureUnit);
  const temperatureMax = formatUnitValue(convertUnitValue(
    140, "fahrenheit", temperatureUnit), temperatureUnit);
  const velocityMin = formatUnitValue(convertUnitValue(
    500, "fps", velocityUnit), velocityUnit);
  const velocityMax = formatUnitValue(convertUnitValue(
    5000, "fps", velocityUnit), velocityUnit);
  const rowNumber = index + 1;
  return `<tr data-mv-temperature-row>
    <td>
      <label class="visually-hidden" for="field-mvtb-temperature-${index}">
        Temperature for measurement ${rowNumber}
      </label>
      <input class="form-control" type="number" inputmode="decimal"
        id="field-mvtb-temperature-${index}" data-mv-temperature-value="temperature"
        data-mv-temperature-canonical="${escapeHtml(row.temperatureFahrenheit ?? "")}"
        value="${escapeHtml(row.temperature)}" min="${temperatureMin}"
        max="${temperatureMax}" step="any" required aria-describedby="field-mvtb-error"
        aria-label="Temperature for measurement ${rowNumber}">
    </td>
    <td>
      <label class="visually-hidden" for="field-mvtb-velocity-${index}">
        Muzzle velocity for measurement ${rowNumber}
      </label>
      <input class="form-control" type="number" inputmode="decimal"
        id="field-mvtb-velocity-${index}" data-mv-temperature-value="velocity"
        data-mv-temperature-canonical="${escapeHtml(row.velocityFps ?? "")}"
        value="${escapeHtml(row.velocity)}" min="${velocityMin}"
        max="${velocityMax}" step="any" required aria-describedby="field-mvtb-error"
        aria-label="Muzzle velocity for measurement ${rowNumber}">
    </td>
    <td class="mv-temperature-remove-cell">
      <button class="btn btn-outline-danger mv-temperature-remove" type="button"
        data-remove-mv-temperature-row aria-label="Remove measurement ${rowNumber}"
        title="Remove measurement"${removable ? "" : " disabled"}>
        <i class="bi bi-trash" aria-hidden="true"></i>
      </button>
    </td>
  </tr>`;
}

function renderMvTemperatureField(calculator, field, saved, values, unitProfile,
  unitOverrides, visibility) {
  const { temperatureUnit, velocityUnit } = getMvTemperatureTableUnits(
    unitProfile,
    unitOverrides[mvTemperatureOverrideKey(calculator, "temperature")],
    unitOverrides[mvTemperatureOverrideKey(calculator, "velocity")],
  );
  let rows;
  try {
    rows = mvTemperatureDisplayRows(saved, temperatureUnit, velocityUnit);
  } catch (_) {
    rows = [{ temperature: "", velocity: "" }];
  }
  const disclosureKey = calculator.slug;
  const expanded = expandedMvTemperatureEditors.has(disclosureKey);
  return `
    <div class="field-control field-control-wide mv-temperature-control"
      data-field-control="${field.name}"${visibility}>
      ${renderFieldLabel(field, `field-${field.name}`, values)}
      <input type="hidden" id="field-${field.name}" name="${field.name}"
        value="${escapeHtml(saved)}" data-canonical-value="${escapeHtml(saved)}">
      <details class="mv-temperature-table-wrap" data-mv-temperature-table
        data-mv-temperature-disclosure-key="${escapeHtml(disclosureKey)}"
        data-temperature-unit="${temperatureUnit}" data-velocity-unit="${velocityUnit}"
        data-max-rows="${field.maxRows || 32}"${expanded ? " open" : ""}>
        <summary class="mv-temperature-summary">
          <span class="mv-temperature-summary-title">Known velocity measurements</span>
          <span class="mv-temperature-summary-value" data-mv-temperature-summary
            aria-live="polite" aria-atomic="true">
            ${escapeHtml(mvTemperatureSummary(rows, temperatureUnit))}
          </span>
          <i class="bi bi-chevron-down mv-temperature-chevron" aria-hidden="true"></i>
        </summary>
        <div class="mv-temperature-editor">
          <div class="mv-temperature-unit-controls" role="group"
            aria-label="MV-Temp table display units">
            <div><span>Temperature units</span>
              ${renderMvTemperatureUnitSelector(field, "temperature", temperatureUnit)}</div>
            <div><span>Velocity units</span>
              ${renderMvTemperatureUnitSelector(field, "velocity", velocityUnit)}</div>
          </div>
          <div class="mv-temperature-table-scroll" tabindex="0"
            aria-label="Known muzzle velocities by temperature">
            <table class="mv-temperature-table">
              <thead><tr>
                <th scope="col">Temperature</th>
                <th scope="col">Muzzle velocity</th>
                <th scope="col"><span class="visually-hidden">Remove</span></th>
              </tr></thead>
              <tbody>
                ${rows.map((row, index) => mvTemperatureRowMarkup(
    row, index, temperatureUnit, velocityUnit, rows.length > 1,
  )).join("")}
              </tbody>
            </table>
          </div>
          <div class="mv-temperature-actions">
            <button class="btn btn-sm btn-outline-secondary responsive-icon-action" type="button"
              data-add-mv-temperature-row aria-label="Add MV-Temp measurement"
              title="Add measurement"${rows.length >= (field.maxRows || 32) ? " disabled" : ""}>
              <i class="bi bi-plus-lg" aria-hidden="true"></i>
              <span class="responsive-action-label">Add measurement</span>
            </button>
            <span class="mv-temperature-count" data-mv-temperature-count>
              ${rows.length} of ${field.maxRows || 32}
            </span>
          </div>
          <div class="invalid-feedback mv-temperature-error" id="field-mvtb-error"
            data-mv-temperature-error role="alert" aria-live="assertive" hidden></div>
        </div>
      </details>
    </div>`;
}

function fieldUnitOverrideKey(calculator, fieldName) {
  return calculatorUsesGunField(calculator, fieldName)
    ? `gun.${fieldName}` : `${calculator.slug}.${fieldName}`;
}

function presetValuesMatch(preset, values) {
  return Object.entries(preset.values).every(([name, expected]) => {
    const actual = values[name];
    if (typeof expected === "boolean") {
      return actual === expected || String(actual) === String(expected);
    }
    return actual !== "" && actual !== null && actual !== undefined
      && Number(actual) === Number(expected);
  });
}

function renderSectionInputPresets(section, sectionIndex, values) {
  const presets = section.inputPresets;
  if (!presets?.options?.length) return "";
  return `<div class="section-input-presets">
    <div class="section-input-preset-heading">
      <span>${escapeHtml(presets.label)}</span>
      <button class="field-help-button" type="button"
        data-field-help="section-preset-${sectionIndex}"
        data-help-title="${escapeHtml(presets.label)}"
        data-help-text="${escapeHtml(presets.help)}"
        aria-label="Help for ${escapeHtml(presets.label)}" aria-expanded="false">?</button>
    </div>
    <div class="section-input-preset-buttons" aria-label="${escapeHtml(presets.label)}">
      ${presets.options.map((preset) => {
    const active = presetValuesMatch(preset, values);
    return `<button class="btn btn-sm btn-outline-secondary${active ? " active" : ""}"
        type="button" data-section-input-preset="${escapeHtml(preset.id)}"
        data-section-index="${sectionIndex}"
        aria-pressed="${active}"
        aria-label="Load ${escapeHtml(preset.label)}: ${escapeHtml(preset.details)}">
        ${escapeHtml(preset.label)}
      </button>`;
  }).join("")}
    </div>
  </div>`;
}

function renderField(calculator, field, values, unitProfile, unitOverrides) {
  const id = `field-${field.name}`;
  const saved = values[field.name];
  const visibility = renderVisibilityAttributes(field, values);
  if (field.type === "hidden") {
    return `<input type="hidden" name="${field.name}" value="${escapeHtml(saved)}">`;
  }
  if (field.type === "checkbox") {
    const checked = saved === true || saved === "true" || saved === "on";
    return `
      <div class="field-control field-control-check" data-field-control="${field.name}"${visibility}>
        <div class="form-check">
          <input class="form-check-input" type="checkbox" id="${id}" name="${field.name}"${checked ? " checked" : ""}>
          <div class="field-check-label-row">
            <label class="form-check-label" for="${id}">${escapeHtml(field.label)}</label>
            ${renderFieldHelp(field, values)}
          </div>
        </div>
      </div>`;
  }
  if (field.type === "temperature-velocity-list") {
    return renderMvTemperatureField(
      calculator, field, saved, values, unitProfile, unitOverrides, visibility,
    );
  }
  if (field.type === "select") {
    return `
      <div class="field-control" data-field-control="${field.name}"${visibility}>
        ${renderFieldLabel(field, id, values)}
        <select class="form-select" id="${id}" name="${field.name}"${field.required ? " required" : ""}>
          ${field.options.map((option) => `<option value="${escapeHtml(option.value)}"${String(saved) === option.value ? " selected" : ""}>${escapeHtml(option.label)}</option>`).join("")}
        </select>
      </div>`;
  }

  const override = unitOverrides[fieldUnitOverrideKey(calculator, field.name)];
  const unitId = field.unitField
    ? getFieldUnitOptions(field).find(({ fieldValue }) => (
      String(fieldValue) === String(values[field.unitField])
    ))?.id || getFieldUnit(field, unitProfile)
    : getFieldUnit(field, unitProfile, override);
  const displayed = unitId ? fieldValueFromCanonical(field, saved, unitId) : saved;
  const displayedConstraints = unitId
    ? fieldDisplayConstraints(field, unitId)
    : { min: field.min, max: field.max, step: field.step };
  const constraints = [
    displayedConstraints.min !== undefined ? `min="${displayedConstraints.min}"` : "",
    displayedConstraints.max !== undefined ? `max="${displayedConstraints.max}"` : "",
    displayedConstraints.step !== undefined ? `step="${displayedConstraints.step}"` : "",
    field.required ? "required" : "",
  ].filter(Boolean).join(" ");
  const unitData = unitId
    ? `data-unit="${unitId}" data-canonical-value="${escapeHtml(saved)}"`
    : "";
  const listControl = ["number-list", "velocity-bc-list"].includes(field.type);
  const rows = field.type === "velocity-bc-list" ? 4 : 2;
  const inputMode = field.type === "velocity-bc-list" ? "text" : "decimal";
  const placeholder = field.placeholder
    ? ` placeholder="${escapeHtml(field.placeholder)}"` : "";
  const control = listControl
    ? `<textarea class="form-control ${field.type}-control" inputmode="${inputMode}" rows="${rows}"
        id="${id}" name="${field.name}" ${unitData}${placeholder}>${escapeHtml(displayed)}</textarea>`
    : `<input class="form-control" type="number" inputmode="decimal" id="${id}" name="${field.name}" value="${escapeHtml(displayed)}" ${unitData} ${constraints}>`;
  const inputControl = field.unit
    ? `<div class="input-group">${control}${renderUnitSelector(field, unitId)}</div>` : control;
  return `
    <div class="field-control${field.wide ? " field-control-wide" : ""}" data-field-control="${field.name}"${visibility}>
      ${renderFieldLabel(field, id, values)}
      ${inputControl}
    </div>`;
}

function visibilityConditions(item) {
  if (Array.isArray(item.showWhenAny)) return item.showWhenAny;
  return item.showWhen ? [item.showWhen] : [];
}

function savedConditionMatches(condition, values) {
  const saved = values[condition.name];
  if (condition.checked !== undefined) {
    const checked = saved === true || saved === "true" || saved === "on";
    return checked === condition.checked;
  }
  return String(saved) === String(condition.value);
}

function renderVisibilityAttributes(item, values) {
  const attributes = [];
  const conditions = visibilityConditions(item);
  const conditionVisible = conditions.length === 0
    || conditions.some((condition) => savedConditionMatches(condition, values));
  if (item.advanced) attributes.push("data-advanced-only");
  if (item.showWhen) {
    attributes.push(`data-show-when-name="${escapeHtml(item.showWhen.name)}"`);
    attributes.push(`data-show-when-value="${escapeHtml(item.showWhen.value)}"`);
  }
  if (item.showWhenAny) {
    attributes.push(`data-show-when-any="${escapeHtml(JSON.stringify(item.showWhenAny))}"`);
  }
  if (conditions.length > 0) {
    attributes.push(`data-condition-hidden="${!conditionVisible}"`);
  }
  if (item.advanced || !conditionVisible) attributes.push("hidden");
  return attributes.length ? ` ${attributes.join(" ")}` : "";
}

function renderCalculatorSection(calculator, section, sectionIndex, values,
  unitProfile, unitOverrides) {
  const renderedFields = section.fields.filter(
    (field) => !field.bottomBar && !calculatorUsesGunField(calculator, field.name));
  if (renderedFields.length === 0) return "";
  if (renderedFields.every((field) => field.type === "hidden")) {
    return renderedFields.map((field) => renderField(
      calculator, field, values, unitProfile, unitOverrides)).join("");
  }
  const id = `section-${calculator.slug}-${sectionIndex}`;
  const expanded = true;
  const displayedSection = renderedFields.every((field) => field.advanced)
    ? { ...section, advanced: true } : section;
  const visibility = renderVisibilityAttributes(displayedSection, values);
  return `
    <fieldset class="calculator-section" data-section-index="${sectionIndex}"
      style="--section-order: ${sectionIndex}"${visibility}>
      <div class="section-header">
        <legend>
          <button class="section-toggle" type="button" data-bs-toggle="collapse"
            data-bs-target="#${id}" aria-controls="${id}" aria-expanded="${expanded}">
            <span><i class="bi ${section.icon} me-2" aria-hidden="true"></i>${escapeHtml(section.title)}</span>
            <i class="bi bi-chevron-down section-chevron" aria-hidden="true"></i>
          </button>
        </legend>
      </div>
      <div class="section-body collapse show" id="${id}"
        data-default-collapsed="false">
        ${renderSectionInputPresets(section, sectionIndex, values)}
        <div class="section-fields">
          ${renderedFields.map((field) => renderField(
            calculator, field, values, unitProfile, unitOverrides)).join("")}
        </div>
      </div>
    </fieldset>`;
}

function renderBottomBarOutput(calculator, values) {
  const field = calculator.sections.flatMap((section) => section.fields)
    .find((candidate) => candidate.bottomBar);
  if (!field) return "";
  return `<div class="form-action-output">
    <label for="field-${field.name}">${escapeHtml(field.label)}</label>
    <select class="form-select form-select-sm" id="field-${field.name}"
      name="${field.name}" aria-label="${escapeHtml(field.label)} mode">
      ${field.options.map((option) => `<option value="${escapeHtml(option.value)}"${String(values[field.name]) === option.value ? " selected" : ""}>${escapeHtml(option.label)}</option>`).join("")}
    </select>
  </div>`;
}

function renderCalculatorSections(calculator, values, unitProfile, unitOverrides) {
  const columns = [[], []];
  calculator.sections.forEach((section, sectionIndex) => {
    columns[sectionIndex % columns.length].push(renderCalculatorSection(
      calculator, section, sectionIndex, values, unitProfile, unitOverrides));
  });
  return `<div class="calculator-sections">
    ${columns.map((sections, columnIndex) => `
      <div class="calculator-section-column" data-section-column="${columnIndex}">
        ${sections.join("")}
      </div>`).join("")}
  </div>`;
}

function fieldHelpText(button, form) {
  if (!button.dataset.helpController) return button.dataset.helpText || "";
  const controller = form.elements.namedItem(button.dataset.helpController);
  try {
    const values = JSON.parse(button.dataset.helpValues || "{}");
    return values[String(controller?.value)] || "";
  } catch (_) {
    return "";
  }
}

function refreshFieldHelp(form) {
  form.querySelectorAll("[data-field-help]").forEach((button) => {
    const text = fieldHelpText(button, form);
    button.dataset.helpText = text;
    const popover = Popover.getInstance(button);
    if (popover?.setContent) {
      popover.setContent({
        ".popover-header": button.dataset.helpTitle,
        ".popover-body": text,
      });
    }
    if (!text || button.closest("[hidden]")) popover?.hide();
  });
}

function disposeFieldHelpPopovers() {
  fieldHelpPopovers.forEach((popover) => popover.dispose());
  fieldHelpPopovers = [];
}

function initializeFieldHelp(form) {
  disposeFieldHelpPopovers();
  refreshFieldHelp(form);
  fieldHelpPopovers = Array.from(form.querySelectorAll("[data-field-help]"), (button) => {
    const popover = new Popover(button, {
      animation: false,
      container: "body",
      customClass: "field-help-popover",
      delay: { show: 80, hide: 100 },
      placement: "auto",
      title: () => button.dataset.helpTitle,
      content: () => button.dataset.helpText,
      trigger: "hover focus",
    });
    button.addEventListener("mouseenter", () => popover.show());
    button.addEventListener("mouseleave", () => {
      if (document.activeElement !== button) popover.hide();
    });
    button.addEventListener("click", () => popover.show());
    button.addEventListener("shown.bs.popover", () => {
      button.setAttribute("aria-expanded", "true");
    });
    button.addEventListener("hidden.bs.popover", () => {
      button.setAttribute("aria-expanded", "false");
    });
    return popover;
  });
}

function desktopCalculatorLayout() {
  if (typeof window.matchMedia === "function") {
    return window.matchMedia(DESKTOP_LAYOUT_QUERY).matches;
  }
  return window.innerWidth >= 992;
}

function sectionFallbackHeight(section) {
  if (section.hidden) return 0;
  const body = section.querySelector(".section-body");
  if (!body?.classList.contains("show")) return 48;
  const controls = Array.from(section.querySelectorAll("[data-field-control]"))
    .filter((control) => !control.hidden);
  let rows = 0;
  let halfRowOpen = false;
  let tallRows = 0;
  controls.forEach((control) => {
    const wide = control.classList.contains("field-control-wide")
      || control.classList.contains("field-control-check");
    if (wide) {
      if (halfRowOpen) rows += 1;
      rows += 1;
      halfRowOpen = false;
    } else if (halfRowOpen) {
      rows += 1;
      halfRowOpen = false;
    } else halfRowOpen = true;
    if (control.querySelector("textarea")) tallRows += 1;
  });
  if (halfRowOpen) rows += 1;
  const presetHeight = section.querySelector(".section-input-presets") ? 58 : 0;
  return 72 + presetHeight + (rows * 58) + (tallRows * 42);
}

function measuredSectionHeight(section) {
  if (section.hidden) return 0;
  const height = section.getBoundingClientRect().height;
  return height > 0 ? height : sectionFallbackHeight(section);
}

function captureSectionRects(form, cached = false) {
  return new Map(Array.from(form.querySelectorAll(".calculator-section"), (section) => [
    section,
    cached ? sectionLayoutRects.get(section) : section.getBoundingClientRect(),
  ]));
}

function reducedMotionPreferred() {
  return typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function animateSectionLayout(sections, previousRects) {
  if (!previousRects || reducedMotionPreferred()) return;
  sections.forEach((section) => {
    if (section.hidden || typeof section.animate !== "function") return;
    const previous = previousRects.get(section);
    const current = section.getBoundingClientRect();
    if (!previous || current.width <= 0 || current.height <= 0) return;
    const deltaX = previous.left - current.left;
    const deltaY = previous.top - current.top;
    if (previous.width <= 0 || previous.height <= 0) {
      section.animate([
        { opacity: 0, transform: "translateY(-0.4rem)" },
        { opacity: 1, transform: "translateY(0)" },
      ], { duration: 220, easing: "cubic-bezier(.2,.8,.2,1)" });
    } else if (Math.abs(deltaX) > 1 || Math.abs(deltaY) > 1) {
      section.animate([
        { transform: `translate(${deltaX}px, ${deltaY}px)` },
        { transform: "translate(0, 0)" },
      ], { duration: 280, easing: "cubic-bezier(.2,.8,.2,1)" });
    }
  });
}

function balanceCalculatorSections(form, { animate = true, previousRects = null } = {}) {
  const layout = form.querySelector(".calculator-sections");
  const columns = Array.from(layout?.querySelectorAll(":scope > .calculator-section-column") || []);
  if (columns.length !== 2) return;
  const sections = Array.from(layout.querySelectorAll(".calculator-section"))
    .sort((left, right) => Number(left.dataset.sectionIndex)
      - Number(right.dataset.sectionIndex));
  const before = previousRects || captureSectionRects(form);

  if (desktopCalculatorLayout()) {
    const heights = sections.map((section) => measuredSectionHeight(section));
    const totals = [0, 0];
    const assignments = [[], []];
    sections.forEach((section, index) => {
      const height = heights[index];
      const target = totals[0] <= totals[1] ? 0 : 1;
      assignments[target].push(section);
      if (height > 0) totals[target] += height + SECTION_COLUMN_GAP;
    });
    assignments.forEach((assigned, columnIndex) => {
      assigned.forEach((section, index) => {
        if (columns[columnIndex].children[index] !== section) {
          columns[columnIndex].append(section);
        }
      });
    });
  } else {
    sections.forEach((section, index) => {
      if (columns[0].children[index] !== section) columns[0].append(section);
    });
  }

  animateSectionLayout(sections, animate ? before : null);
  sections.forEach((section) => {
    sectionLayoutRects.set(section, section.getBoundingClientRect());
  });
}

function queueCalculatorBalance(form, options = {}) {
  const requestFrame = window.requestAnimationFrame
    || ((callback) => window.setTimeout(callback, 0));
  const cancelFrame = window.cancelAnimationFrame || window.clearTimeout;
  if (calculatorLayoutFrame) cancelFrame(calculatorLayoutFrame);
  calculatorLayoutFrame = requestFrame(() => {
    calculatorLayoutFrame = 0;
    if (!form.isConnected) return;
    balanceCalculatorSections(form, options);
  });
}

function setSectionExpanded(section, expanded) {
  const body = section.querySelector(".section-body");
  const toggle = section.querySelector(".section-toggle");
  if (!body || !toggle) return;
  Collapse.getInstance(body)?.dispose();
  body.classList.remove("collapsing");
  body.classList.add("collapse");
  body.classList.toggle("show", expanded);
  body.style.removeProperty("height");
  toggle.classList.toggle("collapsed", !expanded);
  toggle.setAttribute("aria-expanded", String(expanded));
}

function applyAdvancedSectionExpansion(form, enabled) {
  form.querySelectorAll(".calculator-section").forEach((section) => {
    const body = section.querySelector(".section-body");
    const defaultExpanded = body?.dataset.defaultCollapsed !== "true";
    setSectionExpanded(section, enabled || defaultExpanded);
  });
}

function renderAdvancedModeToggle(calculator) {
  if (!calculator.advancedMode) return "";
  const id = `advanced-mode-${calculator.slug}`;
  return `<div class="advanced-mode-control">
    <div class="form-check form-switch mb-0">
      <input class="form-check-input" type="checkbox" role="switch" id="${id}"
        data-advanced-toggle aria-describedby="${id}-help">
      <label class="form-check-label" for="${id}">Advanced mode</label>
    </div>
    <span id="${id}-help">Show additional inputs</span>
  </div>`;
}

function renderPopulateGunAction(calculator) {
  if (!calculator.populateGunParameters) return "";
  const configured = gunLibrary.getStatus().configured;
  return `<div class="populate-gun-control">
    <button class="btn btn-outline-secondary responsive-icon-action" id="populate-from-gun"
      type="button" aria-label="Populate Trajectory inputs from loaded gun"
      title="Populate from gun" aria-describedby="populate-from-gun-message"
      ${configured ? "" : "disabled"}>
      <i class="bi bi-box-arrow-in-down" aria-hidden="true"></i>
      <span class="responsive-action-label">Populate from gun</span>
    </button>
    <span id="populate-from-gun-message" class="populate-gun-message" role="status">
      ${configured
    ? "Copies the current Loaded Gun once; Trajectory remains independent."
    : "Create or load a complete gun to populate Trajectory."}
    </span>
  </div>`;
}

function gunStatusLabel(status = gunLibrary.getStatus()) {
  if (!status.configured) return "No gun loaded";
  if (status.matchingGun) return status.matchingGun.name;
  if (status.selectedGun) return `${status.selectedGun.name} (modified)`;
  return "Unsaved gun settings";
}

function renderCalculatorGunStatus(calculator) {
  if (!calculator.requiresGun) return "";
  const status = gunLibrary.getStatus();
  const state = status.configured ? "ready" : "missing";
  const icon = status.configured ? "bi-check-circle-fill" : "bi-exclamation-circle-fill";
  return `<div class="loaded-gun-status loaded-gun-status-${state}" data-loaded-gun-status="${state}">
    <i class="bi ${icon}" aria-hidden="true"></i>
    <span>${status.configured ? "Loaded gun:" : "Gun required:"}
      <strong>${escapeHtml(gunStatusLabel(status))}</strong></span>
    <a href="#/gun">${status.configured ? "Edit gun" : "Create or load a gun"}</a>
  </div>`;
}

function renderRequiredGunNotice(calculator) {
  if (!calculator.requiresGun || gunLibrary.getStatus().configured) return "";
  return `<div class="alert alert-warning required-gun-notice" role="status">
    <strong>A loaded gun is required.</strong>
    Projectile, sight, zero, and mandatory spin-drift inputs come from
    <a href="#/gun" class="alert-link">Loaded Gun</a>.
  </div>`;
}

function setAdvancedMode(form, enabled) {
  const previousRects = captureSectionRects(form);
  form.dataset.advancedMode = enabled ? "on" : "off";
  form.closest(".calculator-shell")?.classList.toggle("advanced-mode-enabled", enabled);
  applyAdvancedSectionExpansion(form, enabled);
  updateConditionalVisibility(form, { previousRects });
}

function updateConditionalVisibility(form, { animate = true, previousRects = null } = {}) {
  const before = previousRects || captureSectionRects(form);
  form.querySelectorAll(
    "[data-advanced-only], [data-show-when-name], [data-show-when-any]",
  ).forEach((element) => {
    const advancedHidden = element.hasAttribute("data-advanced-only")
      && form.dataset.advancedMode !== "on";
    const controllerName = element.dataset.showWhenName;
    const controller = controllerName ? form.elements.namedItem(controllerName) : null;
    let conditionHidden = controllerName
      && String(controller?.value) !== String(element.dataset.showWhenValue);
    if (element.dataset.showWhenAny) {
      const conditions = JSON.parse(element.dataset.showWhenAny);
      conditionHidden = !conditions.some((condition) => {
        const conditionController = form.elements.namedItem(condition.name);
        if (!conditionController) return false;
        if (condition.checked !== undefined) {
          return Boolean(conditionController.checked) === condition.checked;
        }
        return String(conditionController.value) === String(condition.value);
      });
    }
    if (controllerName || element.dataset.showWhenAny) {
      element.dataset.conditionHidden = conditionHidden ? "true" : "false";
    }
    element.hidden = advancedHidden || conditionHidden;
  });
  form.querySelectorAll("input, select, textarea").forEach((control) => {
    control.disabled = Boolean(control.closest('[data-condition-hidden="true"]'));
  });
  refreshFieldHelp(form);
  balanceCalculatorSections(form, { animate, previousRects: before });
}

function controlAffectsConditionalUi(form, control) {
  const name = control?.name;
  if (!name) return false;
  if (Array.from(form.querySelectorAll("[data-show-when-name]"))
    .some((element) => element.dataset.showWhenName === name)) return true;
  if (Array.from(form.querySelectorAll("[data-help-controller]"))
    .some((element) => element.dataset.helpController === name)) return true;
  return Array.from(form.querySelectorAll("[data-show-when-any]"))
    .some((element) => {
      try {
        return JSON.parse(element.dataset.showWhenAny)
          .some((condition) => condition.name === name);
      } catch (_) {
        return false;
      }
    });
}

function refreshConditionalUiForControl(form, control) {
  if (controlAffectsConditionalUi(form, control)) updateConditionalVisibility(form);
}

function populateCalculatorFromGun(calculator, form) {
  if (!Array.isArray(calculator.populateGunParameters)) return false;
  const state = gunLibrary.getState();
  if (!state.configured) return false;

  for (const name of calculator.populateGunParameters) {
    if (!Object.prototype.hasOwnProperty.call(state.draft, name)) continue;
    const input = form.elements.namedItem(name);
    const field = findCalculatorField(calculator, name);
    if (!input || !field) continue;
    const canonical = String(state.draft[name]);
    if (field.type === "temperature-velocity-list") {
      input.value = canonical;
      input.dataset.canonicalValue = canonical;
      const table = input.closest("[data-field-control]")
        ?.querySelector("[data-mv-temperature-table]");
      if (table) {
        replaceMvTemperatureRows(table, mvTemperatureDisplayRows(
          canonical, table.dataset.temperatureUnit, table.dataset.velocityUnit,
        ));
      }
    } else if (field.type === "checkbox") {
      input.checked = canonical === "true" || canonical === "on";
    } else {
      input.value = input.dataset.unit
        ? fieldValueFromCanonical(field, canonical, input.dataset.unit)
        : canonical;
      if (input.dataset.unit) input.dataset.canonicalValue = canonical;
      input.setCustomValidity("");
    }
  }

  const spinDrift = form.elements.namedItem("sdc");
  if (spinDrift) spinDrift.checked = true;
  updateConditionalVisibility(form, { animate: false });
  saveForm(calculator, form);
  resultCache.delete(calculator.slug);
  resultGunProvenance.delete(calculator.slug);
  renderCalculator(calculator);
  const message = document.querySelector("#populate-from-gun-message");
  if (message) {
    message.textContent = "Copied from Loaded Gun. Later gun changes will not alter Trajectory.";
  }
  return true;
}

function renderCalculator(calculator) {
  const values = readSavedValues(calculator);
  const { unitProfile } = loadSettings();
  const unitOverrides = loadUnitOverrides();
  app.innerHTML = `
    <article class="calculator-shell${calculator.resultMode === "range-table" ? " calculator-shell-wide" : ""}">
      <header class="calculator-heading">
        <div>
          <h1>${escapeHtml(calculator.title)}</h1>
          <p>${escapeHtml(calculator.description)}</p>
          ${renderCalculatorGunStatus(calculator)}
        </div>
        <div class="calculator-heading-actions">
          ${renderPopulateGunAction(calculator)}
          ${renderAdvancedModeToggle(calculator)}
        </div>
      </header>

      ${renderRequiredGunNotice(calculator)}
      <form id="calculator-form" class="calculator-form" novalidate>
        ${renderCalculatorSections(calculator, values, unitProfile, unitOverrides)}
        <div class="form-actions">
          ${renderBottomBarOutput(calculator, values)}
          <div class="form-action-buttons">
          <button class="btn btn-outline-secondary responsive-icon-action" id="reset-form"
            type="button" aria-label="Reset ${escapeHtml(calculator.title)} inputs" title="Reset inputs">
            <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
            <span class="responsive-action-label">Reset</span>
          </button>
          <button class="btn btn-primary responsive-icon-action" id="calculate" type="submit"
            aria-label="Calculate ${escapeHtml(calculator.title)}" title="Calculate"
            ${calculator.requiresGun && !gunLibrary.getStatus().configured ? "disabled" : ""}>
            <i class="bi bi-calculator" aria-hidden="true"></i>
            <span class="responsive-action-label">Calculate</span>
          </button>
          </div>
        </div>
      </form>
      <section id="result-area" class="result-area" aria-live="polite"></section>
    </article>`;

  const form = document.querySelector("#calculator-form");
  initializeMvTemperatureDisclosures(form);
  form.querySelectorAll(".calculator-section").forEach((sectionElement) => {
    updateInputPresetSelection(calculator, form, sectionElement);
  });
  const advancedToggle = document.querySelector("[data-advanced-toggle]");
  if (advancedToggle) {
    form.dataset.advancedMode = "off";
    applyAdvancedSectionExpansion(form, false);
    updateConditionalVisibility(form, { animate: false });
    advancedToggle.addEventListener("change", () => {
      setAdvancedMode(form, advancedToggle.checked);
    });
  } else updateConditionalVisibility(form, { animate: false });
  form.addEventListener("input", (event) => {
    syncInputCanonical(calculator, event.target);
    updateInputPresetSelection(calculator, form, event.target);
    refreshConditionalUiForControl(form, event.target);
    saveForm(calculator, form);
  });
  form.addEventListener("change", (event) => {
    syncInputCanonical(calculator, event.target);
    updateInputPresetSelection(calculator, form, event.target);
    refreshConditionalUiForControl(form, event.target);
    saveForm(calculator, form);
  });
  form.addEventListener("click", (event) => {
    const choice = event.target.closest("[data-unit-choice]");
    if (choice && form.contains(choice)) {
      if (choice.dataset.mvTemperatureAxis) {
        changeMvTemperatureUnit(calculator, form, choice);
      } else changeFieldUnit(calculator, form, choice);
    }
    const preset = event.target.closest("[data-section-input-preset]");
    if (preset && form.contains(preset)) applySectionInputPreset(calculator, form, preset);
    const addMeasurement = event.target.closest("[data-add-mv-temperature-row]");
    if (addMeasurement && form.contains(addMeasurement)) {
      addMvTemperatureRow(
        calculator, form, addMeasurement.closest("[data-mv-temperature-table]"),
      );
    }
    const removeMeasurement = event.target.closest("[data-remove-mv-temperature-row]");
    if (removeMeasurement && form.contains(removeMeasurement)) {
      removeMvTemperatureRow(calculator, form, removeMeasurement);
    }
  });
  let collapseStartRects = null;
  form.addEventListener("show.bs.collapse", (event) => {
    if (event.target.classList.contains("section-body")) {
      collapseStartRects = captureSectionRects(form);
    }
  });
  form.addEventListener("hide.bs.collapse", (event) => {
    if (event.target.classList.contains("section-body")) {
      collapseStartRects = captureSectionRects(form);
    }
  });
  form.addEventListener("shown.bs.collapse", (event) => {
    if (event.target.classList.contains("section-body")) {
      balanceCalculatorSections(form, { previousRects: collapseStartRects });
      collapseStartRects = null;
    }
  });
  form.addEventListener("hidden.bs.collapse", (event) => {
    if (event.target.classList.contains("section-body")) {
      balanceCalculatorSections(form, { previousRects: collapseStartRects });
      collapseStartRects = null;
    }
  });
  form.addEventListener("submit", (event) => calculate(event, calculator, form));
  document.querySelector("#result-area").addEventListener("change", (event) => {
    const selector = event.target.closest("[data-result-unit-select]");
    if (selector) changeResultUnit(calculator, event.currentTarget, selector);
  });
  document.querySelector("#reset-form").addEventListener("click", () => {
    localStorage.removeItem(INPUT_PREFIX + calculator.slug);
    resultCache.delete(calculator.slug);
    resultGunProvenance.delete(calculator.slug);
    renderCalculator(calculator);
  });
  document.querySelector("#populate-from-gun")?.addEventListener("click", () => {
    populateCalculatorFromGun(calculator, form);
  });

  initializeFieldHelp(form);
  queueCalculatorBalance(form, { animate: false });

  const cachedResult = resultCache.get(calculator.slug);
  if (cachedResult) renderResult(cachedResult, document.querySelector("#result-area"), calculator);
}

function renderGunStateSummary({ draftState = "valid" } = {}) {
  if (draftState === "storage-error") {
    return `<div class="gun-state-summary gun-state-summary-storage-error" data-gun-state="storage-error">
      <i class="bi bi-exclamation-octagon-fill" aria-hidden="true"></i>
      <div><strong>Working changes not retained</strong><span>The form still shows your edits, but calculators continue using the last gun successfully written to browser storage.</span></div>
    </div>`;
  }
  if (draftState === "invalid") {
    return `<div class="gun-state-summary gun-state-summary-editing" data-gun-state="editing">
      <i class="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
      <div><strong>Finish editing this gun</strong><span>The current form is incomplete or outside an allowed range. Calculators will not use it until every gun value is valid.</span></div>
    </div>`;
  }
  const status = gunLibrary.getStatus();
  const state = status.configured ? "ready" : "draft";
  const detail = status.matchingGun
    ? `These settings exactly match the saved gun “${status.matchingGun.name}”.`
    : status.selectedGun
      ? `These settings differ from the saved gun “${status.selectedGun.name}”.`
      : status.configured
        ? "These unsaved settings are the gun currently supplied to calculators."
        : "Complete the required fields, enter a name, and choose Save.";
  return `<div class="gun-state-summary gun-state-summary-${state}" data-gun-state="${state}">
    <i class="bi ${status.configured ? "bi-check-circle-fill" : "bi-info-circle-fill"}" aria-hidden="true"></i>
    <div><strong>${escapeHtml(gunStatusLabel(status))}</strong><span>${escapeHtml(detail)}</span></div>
  </div>`;
}

function refreshGunStateSummary(draftState = "valid") {
  const current = document.querySelector("[data-gun-state]");
  if (!current) return;
  const wrapper = document.createElement("div");
  wrapper.innerHTML = renderGunStateSummary({ draftState });
  current.replaceWith(wrapper.firstElementChild);
}

function clearGunStorageError() {
  const alert = document.querySelector("#gun-storage-error");
  if (!alert) return;
  alert.hidden = true;
  alert.textContent = "";
}

function showGunStorageError(action, error) {
  const alert = document.querySelector("#gun-storage-error");
  if (!alert) return;
  alert.textContent = error instanceof GunLibraryPersistenceError
    ? `Could not ${action} because browser storage is unavailable or full. No persisted gun data was changed.`
    : `Could not ${action}. ${error?.message || String(error)}`;
  alert.hidden = false;
}

function runGunStorageMutation(action, mutation) {
  try {
    mutation();
    clearGunStorageError();
    return true;
  } catch (error) {
    showGunStorageError(action, error);
    return false;
  }
}

function downloadJson(filename, contents) {
  const url = URL.createObjectURL(new Blob([contents], { type: "application/json" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function safeDownloadName(value, fallback) {
  const safe = String(value ?? "").trim().replace(/[^a-z0-9._-]+/gi, "-")
    .replace(/^-+|-+$/g, "");
  return safe || fallback;
}

function readImportFile(file) {
  if (!file) return Promise.reject(new TypeError("Choose a JSON file to import."));
  if (typeof file.text === "function") return file.text();
  return new Promise((resolvePromise, rejectPromise) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolvePromise(String(reader.result ?? "")));
    reader.addEventListener("error", () => rejectPromise(
      reader.error || new TypeError("The selected file could not be read."),
    ));
    reader.readAsText(file);
  });
}

function reportNameRequired(input, message) {
  input.setCustomValidity(message);
  input.reportValidity();
  input.addEventListener("input", () => input.setCustomValidity(""), { once: true });
}

function validateGunForm(form) {
  const validLists = validateListFields(gunEditor, form);
  if (!validLists || !form.checkValidity()) {
    form.classList.add("was-validated");
    form.reportValidity();
    return false;
  }
  form.classList.remove("was-validated");
  return true;
}

function clearGunModalState() {
  document.querySelectorAll(".modal.show").forEach((modal) => {
    Modal.getInstance(modal)?.dispose();
    modal.classList.remove("show");
    modal.style.removeProperty("display");
    modal.removeAttribute("aria-modal");
    modal.setAttribute("aria-hidden", "true");
  });
  document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove());
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("overflow");
  document.body.style.removeProperty("padding-right");
}

function renderGun() {
  clearGunModalState();
  migrateLegacyGunSettings();
  const state = gunLibrary.getState();
  const status = gunLibrary.getStatus();
  const keepConfiguredAfterValidEdit = status.configured;
  const { unitProfile } = loadSettings();
  const unitOverrides = loadUnitOverrides();
  const folderGuns = gunLibrary.getGuns(state.selectedFolderId);
  const selectedFolder = state.folders.find(({ id }) => id === state.selectedFolderId)
    || state.folders[0];
  const candidateSelectedId = status.matchingGun?.id || status.selectedGun?.id || "";
  const selectedId = folderGuns.some(({ id }) => id === candidateSelectedId)
    ? candidateSelectedId : folderGuns[0]?.id || "";
  const suggestedName = status.matchingGun?.name || status.selectedGun?.name || "";
  const moveDestinations = state.folders.filter(({ id }) => id !== state.selectedFolderId);
  app.innerHTML = `
    <article class="calculator-shell gun-shell">
      <header class="calculator-heading">
        <div>
          <p class="about-kicker">Shared calculator setup</p>
          <h1>Loaded Gun</h1>
          <p>Define the projectile, sight zero, and required spin-drift data shared by Solution, Range Table, Point Blank Range, and Maximum Distance. Trajectory keeps its own independent setup.</p>
          ${renderGunStateSummary()}
        </div>
      </header>

      <form id="gun-form" class="calculator-form gun-form" novalidate>
        <div class="alert alert-danger gun-storage-error" id="gun-storage-error"
          data-gun-storage-error role="alert" hidden></div>
        <section class="gun-library-card gun-library-launcher" aria-labelledby="gun-library-title">
          <div>
            <h2 id="gun-library-title">Gun library</h2>
            <p>Open a saved gun or manage folders and transfers without crowding the gun editor.</p>
          </div>
          <div class="gun-library-launcher-actions">
            <button class="btn btn-outline-secondary responsive-icon-action" type="button"
              data-bs-toggle="modal" data-bs-target="#open-gun-modal"
              aria-label="Open Gun" title="Open Gun">
              <i class="bi bi-folder2-open" aria-hidden="true"></i>
              <span class="responsive-action-label">Open Gun</span>
            </button>
            <button class="btn btn-outline-secondary responsive-icon-action" type="button"
              data-bs-toggle="modal" data-bs-target="#manage-library-modal"
              aria-label="Manage Library" title="Manage Library">
              <i class="bi bi-collection" aria-hidden="true"></i>
              <span class="responsive-action-label">Manage Library</span>
            </button>
          </div>
        </section>

        ${renderCalculatorSections(gunEditor, state.draft, unitProfile, unitOverrides)}
        <div class="form-actions">
          <div></div>
          <div class="form-action-buttons">
            <button class="btn btn-outline-secondary responsive-icon-action" id="reset-gun"
              type="button" aria-label="Reset gun inputs" title="Reset gun inputs">
              <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
              <span class="responsive-action-label">Reset</span>
            </button>
            <button class="btn btn-primary responsive-icon-action" id="open-save-gun" type="button"
              data-bs-toggle="modal" data-bs-target="#save-gun-modal"
              aria-label="Save gun" title="Save gun">
              <i class="bi bi-floppy" aria-hidden="true"></i>
              <span class="responsive-action-label">Save</span>
            </button>
          </div>
        </div>

        <div class="modal fade" id="save-gun-modal" tabindex="-1" aria-labelledby="save-gun-title" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered"><div class="modal-content">
            <div class="modal-header"><h2 class="modal-title fs-5" id="save-gun-title">Save Gun</h2>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button></div>
            <div class="modal-body gun-library-controls">
              <div class="field-control field-control-wide">
                <label class="form-label" for="gun-name">Gun name</label>
                <input class="form-control" id="gun-name" value="${escapeHtml(suggestedName)}"
                  maxlength="80" placeholder="e.g. .308 Match Rifle">
              </div>
              <div class="field-control field-control-wide">
                <label class="form-label" for="save-gun-folder">Folder</label>
                <select class="form-select" id="save-gun-folder">
                  ${state.folders.map((folder) => `<option value="${escapeHtml(folder.id)}"${folder.id === state.selectedFolderId ? " selected" : ""}>${escapeHtml(folder.name)}</option>`).join("")}
                </select>
              </div>
              <div class="field-control field-control-wide">
                <label class="form-label" for="save-new-gun-folder">Create folder</label>
                <div class="input-group"><input class="form-control" id="save-new-gun-folder" maxlength="80" placeholder="e.g. Competition">
                  <button class="btn btn-outline-secondary" id="save-create-gun-folder" type="button">Add folder</button></div>
              </div>
            </div>
            <div class="modal-footer"><button class="btn btn-secondary" type="button" data-bs-dismiss="modal">Cancel</button>
              <button class="btn btn-primary" id="save-gun" type="button">Save</button></div>
          </div></div>
        </div>

        <div class="modal fade" id="open-gun-modal" tabindex="-1" aria-labelledby="open-gun-title" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered"><div class="modal-content">
            <div class="modal-header"><h2 class="modal-title fs-5" id="open-gun-title">Open Gun</h2>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button></div>
            <div class="modal-body gun-library-controls">
              <div class="field-control field-control-wide">
                <label class="form-label" for="open-gun-folder">Folder</label>
                <select class="form-select" id="open-gun-folder">
                  ${state.folders.map((folder) => `<option value="${escapeHtml(folder.id)}"${folder.id === state.selectedFolderId ? " selected" : ""}>${escapeHtml(folder.name)}</option>`).join("")}
                </select>
              </div>
              <div class="field-control field-control-wide">
                <label class="form-label" for="saved-gun">Saved gun</label>
                <select class="form-select" id="saved-gun" ${folderGuns.length === 0 ? "disabled" : ""}>
                  ${folderGuns.length === 0 ? '<option value="">No saved guns</option>'
    : folderGuns.map((gun) => `<option value="${escapeHtml(gun.id)}"${gun.id === selectedId ? " selected" : ""}>${escapeHtml(gun.name)}</option>`).join("")}
                </select>
              </div>
            </div>
            <div class="modal-footer"><button class="btn btn-outline-danger me-auto" id="delete-gun" type="button" ${folderGuns.length === 0 ? "disabled" : ""}>Delete</button>
              <button class="btn btn-secondary" type="button" data-bs-dismiss="modal">Cancel</button>
              <button class="btn btn-primary" id="load-gun" type="button" ${folderGuns.length === 0 ? "disabled" : ""}>Open</button></div>
          </div></div>
        </div>

        <div class="modal fade" id="manage-library-modal" tabindex="-1" aria-labelledby="manage-library-title" aria-hidden="true">
          <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"><div class="modal-content">
            <div class="modal-header"><h2 class="modal-title fs-5" id="manage-library-title">Manage Library</h2>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button></div>
            <div class="modal-body gun-library-controls">
            <div class="field-control">
              <label class="form-label" for="gun-folder">Selected folder</label>
              <select class="form-select" id="gun-folder">
                ${state.folders.map((folder) => `<option value="${escapeHtml(folder.id)}"${folder.id === state.selectedFolderId ? " selected" : ""}>${escapeHtml(folder.name)}</option>`).join("")}
              </select>
            </div>
            <div class="field-control">
              <label class="form-label" for="new-gun-folder">Create folder</label>
              <div class="input-group">
                <input class="form-control" id="new-gun-folder" maxlength="80"
                  placeholder="e.g. Competition">
                <button class="btn btn-outline-secondary" id="create-gun-folder" type="button">Add folder</button>
              </div>
            </div>
            <div class="field-control field-control-wide">
              <label class="form-label" for="manage-gun">Saved guns in ${escapeHtml(selectedFolder?.name || "folder")}</label>
              <div class="input-group"><select class="form-select" id="manage-gun" ${folderGuns.length === 0 ? "disabled" : ""}>
                  ${folderGuns.length === 0
    ? '<option value="">No saved guns</option>'
    : folderGuns.map((gun) => `<option value="${escapeHtml(gun.id)}"${gun.id === selectedId ? " selected" : ""}>${escapeHtml(gun.name)}</option>`).join("")}
                </select>
              </div>
            </div>
            <div class="field-control field-control-wide">
              <label class="form-label" for="move-gun-folder">Move selected gun</label>
              <div class="input-group">
                <select class="form-select" id="move-gun-folder" ${folderGuns.length === 0 || moveDestinations.length === 0 ? "disabled" : ""}>
                  ${moveDestinations.length === 0
    ? '<option value="">No other folders</option>'
    : moveDestinations.map((folder) => `<option value="${escapeHtml(folder.id)}">${escapeHtml(folder.name)}</option>`).join("")}
                </select>
                <button class="btn btn-outline-secondary" id="move-gun" type="button"
                  ${folderGuns.length === 0 || moveDestinations.length === 0 ? "disabled" : ""}>Move</button>
              </div>
            </div>
            <div class="field-control field-control-wide">
              <span class="form-label">Import and export</span>
              <div class="gun-transfer-actions" role="group" aria-label="Gun import and export actions">
                <button class="btn btn-outline-secondary" id="export-library" type="button">Export Library</button>
                <button class="btn btn-outline-secondary" id="import-library" type="button">Import Library</button>
                <button class="btn btn-outline-secondary" id="export-gun" type="button" ${folderGuns.length === 0 ? "disabled" : ""}>Export Gun</button>
                <button class="btn btn-outline-secondary" id="import-gun" type="button">Import Gun</button>
              </div>
              <input class="visually-hidden" id="import-library-file" type="file"
                accept="application/json,.json">
              <input class="visually-hidden" id="import-gun-file" type="file"
                accept="application/json,.json">
              <div class="form-text">Library imports are placed in Imported. A single gun imports into the selected folder. Matching names are overwritten.</div>
            </div>
          </div>
            <div class="modal-footer"><button class="btn btn-secondary" type="button" data-bs-dismiss="modal">Close</button></div>
          </div></div>
        </div>
      </form>
    </article>`;

  const form = document.querySelector("#gun-form");
  initializeMvTemperatureDisclosures(form);
  updateConditionalVisibility(form, { animate: false });
  form.addEventListener("input", (event) => {
    syncInputCanonical(gunEditor, event.target);
    refreshConditionalUiForControl(form, event.target);
    refreshGunStateSummary(persistGunFormDraft(
      form, keepConfiguredAfterValidEdit,
    ));
  });
  form.addEventListener("change", (event) => {
    syncInputCanonical(gunEditor, event.target);
    refreshConditionalUiForControl(form, event.target);
    refreshGunStateSummary(persistGunFormDraft(
      form, keepConfiguredAfterValidEdit,
    ));
  });
  form.addEventListener("click", (event) => {
    const choice = event.target.closest("[data-unit-choice]");
    if (choice && form.contains(choice)) {
      if (choice.dataset.mvTemperatureAxis) {
        changeMvTemperatureUnit(
          gunEditor, form, choice, keepConfiguredAfterValidEdit,
        );
      } else {
        changeFieldUnit(gunEditor, form, choice, {
          gunConfigured: keepConfiguredAfterValidEdit,
        });
      }
      refreshGunStateSummary(persistGunFormDraft(
        form, keepConfiguredAfterValidEdit,
      ));
    }
    const addMeasurement = event.target.closest("[data-add-mv-temperature-row]");
    if (addMeasurement && form.contains(addMeasurement)) {
      addMvTemperatureRow(
        gunEditor, form, addMeasurement.closest("[data-mv-temperature-table]"),
        keepConfiguredAfterValidEdit,
      );
    }
    const removeMeasurement = event.target.closest("[data-remove-mv-temperature-row]");
    if (removeMeasurement && form.contains(removeMeasurement)) {
      removeMvTemperatureRow(
        gunEditor, form, removeMeasurement, keepConfiguredAfterValidEdit,
      );
    }
  });
  document.querySelector("#save-gun").addEventListener("click", () => {
    if (!validateGunForm(form)) return;
    const name = document.querySelector("#gun-name").value.trim();
    if (!name) {
      const nameInput = document.querySelector("#gun-name");
      reportNameRequired(nameInput, "Enter a name before saving this gun.");
      return;
    }
    if (runGunStorageMutation("save this gun", () => {
      gunLibrary.saveGun(name, {
        values: formValues(gunEditor, form),
        folderId: document.querySelector("#save-gun-folder").value,
      });
    })) renderGun();
  });
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    document.querySelector("#save-gun").click();
  });
  document.querySelector("#open-gun-folder").addEventListener("change", (event) => {
    if (runGunStorageMutation("select this folder", () => {
      gunLibrary.selectFolder(event.target.value);
    })) renderGun();
  });
  document.querySelector("#gun-folder").addEventListener("change", (event) => {
    if (runGunStorageMutation("select this folder", () => {
      gunLibrary.selectFolder(event.target.value);
    })) renderGun();
  });
  document.querySelector("#create-gun-folder").addEventListener("click", () => {
    const input = document.querySelector("#new-gun-folder");
    const name = input.value.trim();
    if (!name) {
      reportNameRequired(input, "Enter a folder name.");
      return;
    }
    if (runGunStorageMutation("create this folder", () => {
      gunLibrary.createFolder(name);
    })) renderGun();
  });
  document.querySelector("#save-create-gun-folder").addEventListener("click", () => {
    const input = document.querySelector("#save-new-gun-folder");
    const name = input.value.trim();
    if (!name) {
      reportNameRequired(input, "Enter a folder name.");
      return;
    }
    if (runGunStorageMutation("create this folder", () => {
      gunLibrary.createFolder(name);
    })) {
      const current = gunLibrary.getState();
      const folder = current.folders.find(({ id }) => id === current.selectedFolderId);
      const destination = document.querySelector("#save-gun-folder");
      if (folder && !Array.from(destination.options).some(({ value }) => value === folder.id)) {
        destination.add(new Option(folder.name, folder.id));
      }
      destination.value = current.selectedFolderId;
      input.value = "";
    }
  });
  document.querySelector("#load-gun").addEventListener("click", () => {
    const id = document.querySelector("#saved-gun").value;
    if (id && runGunStorageMutation("load this gun", () => gunLibrary.loadGun(id))) renderGun();
  });
  document.querySelector("#delete-gun").addEventListener("click", () => {
    const id = document.querySelector("#saved-gun").value;
    if (id && runGunStorageMutation("delete this gun", () => gunLibrary.deleteGun(id))) renderGun();
  });
  document.querySelector("#move-gun").addEventListener("click", () => {
    const id = document.querySelector("#manage-gun").value;
    const folderId = document.querySelector("#move-gun-folder").value;
    if (id && folderId && runGunStorageMutation("move this gun", () => {
      gunLibrary.moveGun(id, folderId);
    })) renderGun();
  });
  document.querySelector("#export-library").addEventListener("click", () => {
    try {
      downloadJson("abc-gun-library.json", gunLibrary.exportLibrary());
      clearGunStorageError();
    } catch (error) {
      showGunStorageError("export this library", error);
    }
  });
  document.querySelector("#export-gun").addEventListener("click", () => {
    const id = document.querySelector("#manage-gun").value;
    const gun = state.guns.find((candidate) => candidate.id === id);
    try {
      downloadJson(
        `${safeDownloadName(gun?.name, "gun")}.abc-gun.json`,
        gunLibrary.exportGun(id),
      );
      clearGunStorageError();
    } catch (error) {
      showGunStorageError("export this gun", error);
    }
  });
  const wireImport = (buttonId, inputId, action, importer) => {
    const input = document.querySelector(`#${inputId}`);
    document.querySelector(`#${buttonId}`).addEventListener("click", () => input.click());
    input.addEventListener("change", async () => {
      try {
        const serialized = await readImportFile(input.files?.[0]);
        importer(serialized);
        clearGunStorageError();
        input.value = "";
        renderGun();
      } catch (error) {
        input.value = "";
        showGunStorageError(action, error);
      }
    });
  };
  wireImport("import-library", "import-library-file", "import this library", (serialized) => {
    gunLibrary.importLibrary(serialized);
  });
  wireImport("import-gun", "import-gun-file", "import this gun", (serialized) => {
    gunLibrary.importGun(serialized);
  });
  document.querySelector("#reset-gun").addEventListener("click", () => {
    if (runGunStorageMutation("reset the working gun", () => gunLibrary.resetDraft())) renderGun();
  });

  initializeFieldHelp(form);
  queueCalculatorBalance(form, { animate: false });
}

function formValues(calculator, form) {
  const values = {};
  for (const section of calculator.sections) {
    for (const field of section.fields) {
      if (calculatorUsesGunField(calculator, field.name)) continue;
      const element = form.elements.namedItem(field.name);
      if (!element) continue;
      if (field.type === "checkbox") values[field.name] = element.checked;
      else if (getFieldUnitGroup(field) && element.dataset.unit) {
        values[field.name] = element.dataset.canonicalValue;
      } else values[field.name] = element.value;
    }
  }
  return values;
}

function saveForm(calculator, form, { gunConfigured } = {}) {
  const values = formValues(calculator, form);
  if (calculator.slug === gunEditor.slug) {
    gunLibrary.setDraft(values, gunConfigured === undefined
      ? {} : { configured: gunConfigured });
  } else {
    localStorage.setItem(INPUT_PREFIX + calculator.slug, JSON.stringify(values));
  }
}

function persistGunFormDraft(form, configured) {
  const listsValid = validateListFields(gunEditor, form);
  if (!listsValid || !form.checkValidity()) {
    try {
      if (gunLibrary.getStatus().configured) gunLibrary.setConfigured(false);
      clearGunStorageError();
      return "invalid";
    } catch (error) {
      showGunStorageError("retain these working-gun changes", error);
      return "storage-error";
    }
  }
  try {
    saveForm(gunEditor, form, { gunConfigured: configured });
    clearGunStorageError();
    return "valid";
  } catch (error) {
    showGunStorageError("retain these working-gun changes", error);
    return "storage-error";
  }
}

function inputPresetMatchesForm(section, preset, form) {
  return Object.entries(preset.values).every(([name, expected]) => {
    const field = section.fields.find((candidate) => candidate.name === name);
    const control = form.elements.namedItem(name);
    if (!field || !control) return false;
    if (field.type === "checkbox") return control.checked === Boolean(expected);
    const expectedDisplay = control.dataset.unit
      ? fieldValueFromCanonical(field, expected, control.dataset.unit)
      : String(expected);
    return control.value === expectedDisplay
      || (control.value !== "" && expectedDisplay !== ""
        && Number(control.value) === Number(expectedDisplay));
  });
}

function updateInputPresetSelection(calculator, form, control) {
  const sectionElement = control?.closest?.(".calculator-section");
  if (!sectionElement?.querySelector("[data-section-input-preset]")) return;
  const section = calculator.sections[Number(sectionElement.dataset.sectionIndex)];
  if (!section?.inputPresets) return;
  const activePreset = section.inputPresets.options
    .find((preset) => inputPresetMatchesForm(section, preset, form));
  sectionElement.querySelectorAll("[data-section-input-preset]").forEach((button) => {
    const active = button.dataset.sectionInputPreset === activePreset?.id;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
  });
}

function applySectionInputPreset(calculator, form, choice) {
  const section = calculator.sections[Number(choice.dataset.sectionIndex)];
  const preset = section?.inputPresets?.options
    ?.find(({ id }) => id === choice.dataset.sectionInputPreset);
  if (!preset) return;

  Object.entries(preset.values).forEach(([name, value]) => {
    const field = section.fields.find((candidate) => candidate.name === name);
    const control = form.elements.namedItem(name);
    if (!field || !control) return;
    if (field.type === "checkbox") {
      control.checked = Boolean(value);
    } else {
      const canonicalValue = String(value);
      control.value = control.dataset.unit
        ? fieldValueFromCanonical(field, canonicalValue, control.dataset.unit)
        : canonicalValue;
      if (control.dataset.unit) control.dataset.canonicalValue = canonicalValue;
      control.setCustomValidity("");
    }
  });

  form.classList.remove("was-validated");
  updateInputPresetSelection(calculator, form, choice);
  updateConditionalVisibility(form);
  saveForm(calculator, form);
}

function findCalculatorField(calculator, name) {
  if (calculatorUsesGunField(calculator, name)) return findGunField(name);
  return calculator.sections.flatMap((section) => section.fields)
    .find((field) => field.name === name);
}

function mvTemperatureTableRows(table) {
  return Array.from(table.querySelectorAll("[data-mv-temperature-row]"), (row) => {
    const temperature = row.querySelector('[data-mv-temperature-value="temperature"]');
    const velocity = row.querySelector('[data-mv-temperature-value="velocity"]');
    return {
      temperature: temperature?.value ?? "",
      velocity: velocity?.value ?? "",
      temperatureFahrenheit: temperature?.dataset.mvTemperatureCanonical ?? "",
      velocityFps: velocity?.dataset.mvTemperatureCanonical ?? "",
    };
  });
}

function mvTemperatureCanonicalRows(table) {
  return Array.from(table.querySelectorAll("[data-mv-temperature-row]"), (row) => {
    const temperature = row.querySelector('[data-mv-temperature-value="temperature"]');
    const velocity = row.querySelector('[data-mv-temperature-value="velocity"]');
    const temperatureCanonical = temperature.dataset.mvTemperatureCanonical;
    const velocityCanonical = velocity.dataset.mvTemperatureCanonical;
    const displayedTemperature = temperatureCanonical === "" ? "" : formatUnitValue(
      convertUnitValue(
        temperatureCanonical, "fahrenheit", table.dataset.temperatureUnit,
      ), table.dataset.temperatureUnit,
    );
    const displayedVelocity = velocityCanonical === "" ? "" : formatUnitValue(
      convertUnitValue(velocityCanonical, "fps", table.dataset.velocityUnit),
      table.dataset.velocityUnit,
    );
    const temperatureChanged = temperature.dataset.mvTemperatureDirty === "true"
      || temperatureCanonical === "" || temperature.value !== displayedTemperature;
    const velocityChanged = velocity.dataset.mvTemperatureDirty === "true"
      || velocityCanonical === "" || velocity.value !== displayedVelocity;
    return {
      temperature: temperatureChanged ? temperature.value : temperatureCanonical,
      velocity: velocityChanged ? velocity.value : velocityCanonical,
      temperatureUnit: temperatureChanged ? table.dataset.temperatureUnit : "fahrenheit",
      velocityUnit: velocityChanged ? table.dataset.velocityUnit : "fps",
    };
  });
}

function updateMvTemperatureSummary(table) {
  const summary = table.querySelector("[data-mv-temperature-summary]");
  if (!summary) return;
  const rows = mvTemperatureTableRows(table);
  const text = mvTemperatureSummary(rows, table.dataset.temperatureUnit);
  summary.textContent = table.classList.contains("is-invalid")
    ? `${text} · needs attention` : text;
}

function openMvTemperatureEditor(table) {
  if (!table.open) table.open = true;
  expandedMvTemperatureEditors.add(table.dataset.mvTemperatureDisclosureKey);
}

function setMvTemperatureTableValidity(table, message = "") {
  const controls = Array.from(table.querySelectorAll("[data-mv-temperature-value]"));
  controls.forEach((control) => {
    control.setCustomValidity("");
    control.removeAttribute("aria-invalid");
  });
  if (message && controls.length > 0) {
    const invalidControl = controls.find(
      (control) => control.dataset.mvTemperatureDirty === "true",
    ) || controls.find((control) => !control.validity.valid) || controls[0];
    invalidControl.setCustomValidity(message);
    invalidControl.setAttribute("aria-invalid", "true");
    openMvTemperatureEditor(table);
  }
  table.classList.toggle("is-invalid", Boolean(message));
  const error = table.querySelector("[data-mv-temperature-error]");
  if (error) {
    error.textContent = message;
    error.hidden = !message;
  }
  updateMvTemperatureSummary(table);
}

function syncMvTemperatureTable(form, editedInput = null) {
  const table = form.querySelector("[data-mv-temperature-table]");
  if (!table) return true;
  const hidden = form.elements.namedItem("mvtb");
  if (editedInput?.matches?.("[data-mv-temperature-value]")) {
    editedInput.dataset.mvTemperatureDirty = "true";
  }
  try {
    const rows = mvTemperatureCanonicalRows(table);
    const rowCanonicalValues = rows.map((row) => {
      const pair = mvTemperatureRowsToCanonical([{
        temperature: row.temperature,
        velocity: row.velocity,
      }], row.temperatureUnit, row.velocityUnit);
      const [temperature, velocity] = pair.split(":");
      return { temperature, velocity };
    });
    const canonical = mvTemperatureRowsToCanonical(
      rowCanonicalValues, "fahrenheit", "fps",
    );
    hidden.value = canonical;
    hidden.dataset.canonicalValue = canonical;
    Array.from(table.querySelectorAll("[data-mv-temperature-row]")).forEach(
      (row, index) => {
        const temperature = row.querySelector('[data-mv-temperature-value="temperature"]');
        const velocity = row.querySelector('[data-mv-temperature-value="velocity"]');
        temperature.dataset.mvTemperatureCanonical = rowCanonicalValues[index].temperature;
        velocity.dataset.mvTemperatureCanonical = rowCanonicalValues[index].velocity;
        delete temperature.dataset.mvTemperatureDirty;
        delete velocity.dataset.mvTemperatureDirty;
      },
    );
    setMvTemperatureTableValidity(table);
    return true;
  } catch (error) {
    setMvTemperatureTableValidity(table, error.message || String(error));
    return false;
  }
}

function updateMvTemperatureRowControls(table) {
  const rows = Array.from(table.querySelectorAll("[data-mv-temperature-row]"));
  rows.forEach((row, index) => {
    const rowNumber = index + 1;
    for (const axis of ["temperature", "velocity"]) {
      const input = row.querySelector(`[data-mv-temperature-value="${axis}"]`);
      if (!input) continue;
      input.id = `field-mvtb-${axis}-${index}`;
      input.setAttribute("aria-label", `${axis === "temperature" ? "Temperature" : "Muzzle velocity"} for measurement ${rowNumber}`);
    }
    const remove = row.querySelector("[data-remove-mv-temperature-row]");
    if (remove) {
      remove.disabled = rows.length === 1;
      remove.setAttribute("aria-label", `Remove measurement ${rowNumber}`);
    }
  });
  const maximum = Number(table.dataset.maxRows || 32);
  const add = table.querySelector("[data-add-mv-temperature-row]");
  if (add) add.disabled = rows.length >= maximum;
  const count = table.querySelector("[data-mv-temperature-count]");
  if (count) count.textContent = `${rows.length} of ${maximum}`;
  updateMvTemperatureSummary(table);
}

function initializeMvTemperatureDisclosures(form) {
  form.querySelectorAll("[data-mv-temperature-table]").forEach((table) => {
    table.addEventListener("toggle", () => {
      const key = table.dataset.mvTemperatureDisclosureKey;
      if (table.open) expandedMvTemperatureEditors.add(key);
      else expandedMvTemperatureEditors.delete(key);
      queueCalculatorBalance(form);
    });
  });
}

function replaceMvTemperatureRows(table, rows) {
  const body = table.querySelector("tbody");
  body.innerHTML = rows.map((row, index) => mvTemperatureRowMarkup(
    row,
    index,
    table.dataset.temperatureUnit,
    table.dataset.velocityUnit,
    rows.length > 1,
  )).join("");
  updateMvTemperatureRowControls(table);
  setMvTemperatureTableValidity(table);
}

function persistMvTemperatureEdit(calculator, form, gunConfigured) {
  if (calculator.slug === gunEditor.slug) {
    refreshGunStateSummary(persistGunFormDraft(form, gunConfigured));
  } else {
    saveForm(calculator, form);
  }
}

function addMvTemperatureRow(calculator, form, table, gunConfigured) {
  const rows = mvTemperatureTableRows(table);
  if (rows.length >= Number(table.dataset.maxRows || 32)) return;
  rows.push({ temperature: "", velocity: "" });
  replaceMvTemperatureRows(table, rows);
  syncMvTemperatureTable(form);
  persistMvTemperatureEdit(calculator, form, gunConfigured);
  table.querySelector("[data-mv-temperature-row]:last-child input")?.focus();
  queueCalculatorBalance(form);
}

function removeMvTemperatureRow(calculator, form, button, gunConfigured) {
  const table = button.closest("[data-mv-temperature-table]");
  const rows = mvTemperatureTableRows(table);
  if (rows.length <= 1) return;
  const index = Array.from(table.querySelectorAll("[data-mv-temperature-row]"))
    .indexOf(button.closest("[data-mv-temperature-row]"));
  rows.splice(index, 1);
  replaceMvTemperatureRows(table, rows);
  syncMvTemperatureTable(form);
  persistMvTemperatureEdit(calculator, form, gunConfigured);
  queueCalculatorBalance(form);
}

function changeMvTemperatureUnit(calculator, form, choice, gunConfigured) {
  const table = choice.closest("[data-mv-temperature-table]");
  const axis = choice.dataset.mvTemperatureAxis;
  const nextUnit = choice.dataset.unitChoice;
  if (!table || !["temperature", "velocity"].includes(axis) || !nextUnit) return;
  const tableInvalid = table.classList.contains("is-invalid")
    || Array.from(table.querySelectorAll("[data-mv-temperature-value]"))
      .some((input) => !input.checkValidity());
  if (tableInvalid) return;

  const hidden = form.elements.namedItem("mvtb");
  const temperatureUnit = axis === "temperature"
    ? nextUnit : table.dataset.temperatureUnit;
  const velocityUnit = axis === "velocity" ? nextUnit : table.dataset.velocityUnit;
  const rows = mvTemperatureDisplayRows(
    hidden.dataset.canonicalValue, temperatureUnit, velocityUnit,
  );
  table.dataset.temperatureUnit = temperatureUnit;
  table.dataset.velocityUnit = velocityUnit;
  replaceMvTemperatureRows(table, rows);
  setUnitOverride(calculator.slug, `mvtb.${axis}`, nextUnit);
  if (axis === "velocity") {
    const consumers = calculator.slug === gunEditor.slug
      ? calculatorGroups.flatMap((group) => group.calculators)
        .filter(({ requiresGun }) => requiresGun)
      : [calculator];
    consumers.forEach(({ slug }) => {
      clearUnitOverridesForPrefix(slug, "output.projectileVelocity.");
      setUnitOverride(slug, "inputType.projectileVelocity", nextUnit);
    });
  }

  const selector = table.querySelector(`[data-unit-selector="mvtb.${axis}"]`);
  if (selector) selector.querySelector("[data-unit-label]").textContent = getUnitLabel(nextUnit);
  table.querySelectorAll(`[data-mv-temperature-axis="${axis}"]`).forEach((option) => {
    const active = option.dataset.unitChoice === nextUnit;
    option.classList.toggle("active", active);
    option.setAttribute("aria-pressed", String(active));
  });
  persistMvTemperatureEdit(calculator, form, gunConfigured);

  const cachedResult = resultCache.get(calculator.slug);
  const resultArea = document.querySelector("#result-area");
  if (cachedResult && resultArea) {
    renderResult(cachedResult, resultArea, calculator, { scroll: false });
  }
}

function calculatorFields(calculator) {
  const fields = calculator.sections.flatMap((section) => section.fields);
  if (!calculator.requiresGun) return fields;
  const byName = new Map(fields.map((field) => [field.name, field]));
  calculator.gunParameters.forEach((name) => {
    const shared = findGunField(name);
    if (shared) byName.set(name, shared);
  });
  return [...byName.values()];
}

function syncInputCanonical(calculator, input) {
  const form = input?.closest?.("form");
  if (form && input.closest?.("[data-mv-temperature-table]")) {
    syncMvTemperatureTable(form, input);
    return;
  }
  if (!input?.name || !input.dataset?.unit) return;
  const field = findCalculatorField(calculator, input.name);
  if (field && getFieldUnitGroup(field)) {
    input.dataset.canonicalValue = fieldValueToCanonical(field, input.value, input.dataset.unit);
  }
}

function applyInputConstraints(input, constraints) {
  for (const attribute of ["min", "max", "step"]) {
    const value = constraints[attribute];
    if (value === undefined) input.removeAttribute(attribute);
    else input.setAttribute(attribute, value);
  }
}

function validateListFields(calculator, form) {
  let valid = true;
  for (const field of calculatorFields(calculator).filter(
    ({ type }) => [
      "number-list", "velocity-bc-list", "temperature-velocity-list",
    ].includes(type))) {
    const input = form.elements.namedItem(field.name);
    if (!input) continue;
    if (field.type === "temperature-velocity-list") {
      if (!syncMvTemperatureTable(form)) valid = false;
      continue;
    }
    input.setCustomValidity("");
    try {
      if (field.type === "velocity-bc-list") {
        if (form.elements.namedItem("bcm")?.value !== "3") continue;
        const canonicalValue = velocityBcFieldValueToCanonical(
          field, input.value, input.dataset.unit,
        );
        if (!canonicalValue) throw new RangeError("Enter at least one custom velocity band.");
        input.dataset.canonicalValue = canonicalValue;
      } else {
        const canonical = fieldValueToCanonical(field, input.value, input.dataset.unit);
        const values = parseTemperatureList(canonical);
        if (field.minItems && values.length < field.minItems) {
          throw new RangeError(`Enter at least ${field.minItems} unique values.`);
        }
        if (field.maxItems && values.length > field.maxItems) {
          throw new RangeError(`Enter no more than ${field.maxItems} unique values.`);
        }
        input.dataset.canonicalValue = values.join(",");
      }
    } catch (error) {
      input.setCustomValidity(error.message || String(error));
      valid = false;
    }
  }
  return valid;
}

function changeFieldUnit(calculator, form, choice, { gunConfigured } = {}) {
  const field = findCalculatorField(calculator, choice.dataset.fieldName);
  const input = form.elements.namedItem(choice.dataset.fieldName);
  const nextUnit = choice.dataset.unitChoice;
  if (!field || !input || !input.dataset.unit || !nextUnit) return;

  const canonicalValue = input.dataset.canonicalValue;
  if (!field.unitField) {
    input.value = fieldValueFromCanonical(field, canonicalValue, nextUnit);
  }
  input.dataset.unit = nextUnit;
  input.dataset.canonicalValue = canonicalValue;
  applyInputConstraints(input, fieldDisplayConstraints(field, nextUnit));

  if (field.unitField) {
    const unitField = form.elements.namedItem(field.unitField);
    if (unitField && choice.dataset.unitFieldValue) {
      unitField.value = choice.dataset.unitFieldValue;
    }
  }

  const selector = form.querySelector(`[data-unit-selector="${field.name}"]`);
  if (selector) selector.querySelector("[data-unit-label]").textContent = getUnitLabel(nextUnit);
  form.querySelectorAll(`[data-field-name="${field.name}"]`).forEach((option) => {
    const active = option.dataset.unitChoice === nextUnit;
    option.classList.toggle("active", active);
    option.setAttribute("aria-pressed", String(active));
  });

  if (!field.unitField) {
    const correlation = getFieldUnitCorrelation(field);
    if (correlation) {
      const consumers = calculator.slug === gunEditor.slug
        ? calculatorGroups.flatMap((group) => group.calculators)
          .filter(({ requiresGun }) => requiresGun)
        : [calculator];
      consumers.forEach(({ slug }) => {
        clearUnitOverridesForPrefix(slug, `output.${correlation}.`);
      });
      setUnitOverride(calculator.slug, `inputType.${correlation}`, nextUnit);
    }
    setUnitOverride(calculator.slug, field.name, nextUnit);
  }
  if (calculator.slug === gunEditor.slug) {
    persistGunFormDraft(form, gunConfigured);
  } else saveForm(calculator, form);

  const cachedResult = resultCache.get(calculator.slug);
  const resultArea = document.querySelector("#result-area");
  if (cachedResult && resultArea) {
    renderResult(cachedResult, resultArea, calculator, { scroll: false });
  }
}

function canonicalFormData(calculator, form) {
  const data = new FormData(form);
  if (data.has("outputMode")) {
    data.set("un", data.get("outputMode") === "linear" ? "1" : "3");
    data.delete("outputMode");
  }
  for (const section of calculator.sections) {
    for (const field of section.fields) {
      const input = form.elements.namedItem(field.name);
      if (!input || calculatorUsesGunField(calculator, field.name)) continue;
      if (field.type === "velocity-bc-list"
        && form.elements.namedItem("bcm")?.value !== "3") continue;
      if (data.has(field.name) && getFieldUnitGroup(field) && input.dataset.unit) {
        const renderedCanonicalValue = fieldValueFromCanonical(
          field, input.dataset.canonicalValue, input.dataset.unit);
        const displayedValueIsCurrent = input.value === renderedCanonicalValue
          || (input.value !== "" && renderedCanonicalValue !== ""
            && Number(input.value) === Number(renderedCanonicalValue));
        const canonicalValue = displayedValueIsCurrent
          ? input.dataset.canonicalValue
          : fieldValueToCanonical(field, input.value, input.dataset.unit);
        input.dataset.canonicalValue = canonicalValue;
        data.set(field.name, canonicalValue);
      }
      if (data.has(field.name)) {
        data.set(field.name, zeroSafeOptionalFieldValue(
          field, data.get(field.name),
        ));
      }
    }
  }
  return data;
}

function appendLoadedGunParameters(calculator, formData, gunState) {
  const requestValues = Object.fromEntries(formData.entries());
  const gunMerged = mergeLoadedGunValues(calculator, requestValues, gunState);
  const merged = calculator.requiresGun
    ? gunMerged : applyMvTemperatureModel(calculator, gunMerged);
  for (const name of [...formData.keys()]) formData.delete(name);
  for (const [name, value] of Object.entries(merged)) {
    formData.set(name, value);
  }
  return formData;
}

function validateCalculatorGunCompatibility(calculator, formData) {
  validateLoadedGunCompatibility(calculator, Object.fromEntries(formData.entries()));
}

function calculatorGunProvenance(calculator, gunState = gunLibrary.getState()) {
  if (!calculator.requiresGun) return null;
  const status = gunLibrary.getStatus();
  return {
    fingerprint: gunLibrary.getDraftFingerprint(),
    label: gunStatusLabel(status),
    configured: gunState.configured,
  };
}

async function runEngagementZone(formData, calculator) {
  const body = encodeCgiParameters(
    new URLSearchParams(formData), `${calculator.title} — Engagement Zone`,
  );
  const output = await executeCgi("engagement-zone", body);
  return extractEngagementZoneResult(parseCgiResponse(output));
}

async function calculate(event, calculator, form) {
  event.preventDefault();
  if (calculator.requiresGun && !gunLibrary.getStatus().configured) return;
  if (!validateListFields(calculator, form) || !form.checkValidity()) {
    form.classList.add("was-validated");
    form.reportValidity();
    return;
  }

  const resultArea = document.querySelector("#result-area");
  const submit = document.querySelector("#calculate");
  submit.disabled = true;
  submit.setAttribute("aria-busy", "true");
  submit.setAttribute("aria-label", `Calculating ${calculator.title}`);
  submit.innerHTML = '<span class="spinner-border spinner-border-sm" aria-hidden="true"></span><span class="responsive-action-label">Calculating…</span>';
  resultArea.innerHTML = '<div class="loading-panel"><div><span class="spinner-border spinner-border-sm me-2"></span>Loading WebAssembly engine…</div></div>';

  try {
    const gunState = gunLibrary.getState();
    const provenance = calculatorGunProvenance(calculator, gunState);
    let formData = appendLoadedGunParameters(
      calculator, canonicalFormData(calculator, form), gunState);
    if (calculator.slug === "trajectory") {
      formData = prepareTrajectoryParameters(formData);
    }
    validateCalculatorGunCompatibility(calculator, formData);
    const runTrajectory = async (parameters, caseTitle) => {
        const body = encodeCgiParameters(parameters,
          `${calculator.title} — ${caseTitle}`);
        const output = await executeCgi(calculator.module, body);
        const parsed = parseCgiResponse(output);
        const zeroRangeYards = Number(parameters.get("rz"));
        return Number.isFinite(zeroRangeYards) && zeroRangeYards > 0
          ? {
            ...parsed,
            metadata: { ...(parsed.metadata || {}), zeroRangeYards },
          }
          : parsed;
      };
    let result;
    if (calculator.resultMode === "range-table") {
      result = await calculateRangeTable(formData, runTrajectory);
    } else if (calculator.resultMode === "solution") {
      result = await calculateSolution(formData, runTrajectory);
      if (!result.error && solutionTargetSection(result)) {
        try {
          submit.setAttribute("aria-label", "Simulating 1,000-shot Engagement Zone");
          submit.innerHTML = '<span class="spinner-border spinner-border-sm" aria-hidden="true"></span><span class="responsive-action-label">Simulating…</span>';
          resultArea.innerHTML = '<div class="loading-panel" role="status"><div><span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>Running the 1,000-shot C/WASM Engagement Zone simulation…</div></div>';
          result.engagementZone = await runEngagementZone(formData, calculator);
        } catch (error) {
          result.engagementZone = { error: error.message || String(error) };
        }
      }
    } else if (calculator.slug === "trajectory") {
      result = await runTrajectory(formData, "Trajectory table");
      const denseIncrement = solutionRangeIncrement(Number(formData.get("rx")));
      if (!result.error && Number(formData.get("ri")) > denseIncrement) {
        const chartParameters = new URLSearchParams(formData);
        chartParameters.set("ri", String(denseIncrement));
        result.chartResult = await runTrajectory(chartParameters, "Trajectory chart");
      }
    } else {
      result = parseCgiResponse(await executeCgi(calculator.module,
        encodeCgiParameters(formData, calculator.title)));
    }
    if (!result.error) {
      resultCache.set(calculator.slug, result);
      if (provenance) {
        resultGunProvenance.set(calculator.slug, {
          ...provenance,
          requestSnapshot: new URLSearchParams(formData).toString(),
        });
      }
    } else {
      resultCache.delete(calculator.slug);
      resultGunProvenance.delete(calculator.slug);
    }
    renderResult(result, resultArea, calculator);
  } catch (error) {
    resultCache.delete(calculator.slug);
    resultGunProvenance.delete(calculator.slug);
    renderResult({ error: error.message || String(error), sections: [] }, resultArea, calculator);
  } finally {
    submit.disabled = false;
    submit.removeAttribute("aria-busy");
    submit.setAttribute("aria-label", `Calculate ${calculator.title}`);
    submit.innerHTML = '<i class="bi bi-calculator" aria-hidden="true"></i><span class="responsive-action-label">Calculate</span>';
  }
}

function resultNumber(value) {
  const normalized = String(value ?? "").replaceAll(",", "").trim();
  if (!normalized) return null;
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : null;
}

function resultUnitKey(sectionIndex, tableIndex, suffix) {
  return `s${sectionIndex}.t${tableIndex}.${suffix}`;
}

function resultUnitModel(calculator, unitLabel, valueLabel, unitProfile,
  unitOverrides, key) {
  const resultGroup = getResultUnitGroup(unitLabel, valueLabel);
  if (!resultGroup) return null;
  const fieldOverrides = Object.fromEntries(calculatorFields(calculator)
    .map((field) => [field.name, unitOverrides[fieldUnitOverrideKey(calculator, field.name)]]));
  const correlation = getResultUnitCorrelation(unitLabel, valueLabel);
  const override = unitOverrides[
    `${calculator.slug}.output.${correlation}.${key}`
  ];
  const preferredInputUnit = correlation
    ? unitOverrides[`${calculator.slug}.inputType.${correlation}`]
      || (calculator.requiresGun
        ? unitOverrides[`gun.inputType.${correlation}`] : null)
    : null;
  const mvTemperatureVelocityUnit = correlation === "projectileVelocity"
    && calculatorFields(calculator).some(({ name }) => name === "mvtb")
    ? getMvTemperatureTableUnits(
      unitProfile,
      null,
      unitOverrides[`${calculator.requiresGun ? "gun" : calculator.slug}.mvtb.velocity`],
    ).velocityUnit
    : null;
  const correlatedUnit = mvTemperatureVelocityUnit || getCorrelatedResultUnit(
    calculatorFields(calculator), unitLabel, valueLabel, unitProfile,
    fieldOverrides, preferredInputUnit);
  const normalizedValueLabel = String(valueLabel).replace(/:$/, "").trim().toLowerCase();
  const outputControlled = ["trajectory", "solution", "range-table"].includes(calculator.slug)
    && calculator.sourceControlledResultUnits?.some(
      (label) => label.toLowerCase() === normalizedValueLabel);
  const preferredUnit = outputControlled
    && (resultGroup.canonical === "moa" || resultGroup.canonical === "natoMil")
    ? "mrad"
    : correlatedUnit;
  const unitId = getResultUnit(
    unitLabel, valueLabel, unitProfile, override,
    preferredUnit);
  let options = getResultUnitOptions(unitLabel, valueLabel);
  if (outputControlled) {
    if (resultGroup.canonical === "moa" || resultGroup.canonical === "natoMil") {
      options = options.filter(({ id }) => ["mrad", "moa", "natoMil"].includes(id));
    } else if (resultGroup.canonical === "inch") {
      options = options.filter(({ id }) => (
        ["inch", "millimeter", "centimeter", "meter"].includes(id)
      ));
    }
  }
  return {
    canonicalUnit: resultGroup.canonical,
    options,
    correlation,
    unitId,
  };
}

function renderResultUnitSelect(model, key, valueLabel) {
  if (model.options.length < 2) {
    return `<span class="result-unit-static">${escapeHtml(getUnitLabel(model.unitId))}</span>`;
  }
  return `
    <select class="form-select form-select-sm result-unit-select" data-result-unit-select
      data-result-unit-key="${escapeHtml(key)}"
      data-result-unit-correlation="${escapeHtml(model.correlation)}"
      aria-label="Change unit for ${escapeHtml(String(valueLabel).replace(/:$/, ""))}">
      ${model.options.map((option) => `<option value="${option.id}"${option.id === model.unitId ? " selected" : ""}>${escapeHtml(option.label)}</option>`).join("")}
    </select>`;
}

function renderConvertedResultValue(value, model, key) {
  const displayed = formatUnitValue(
    convertUnitValue(value, model.canonicalUnit, model.unitId), model.unitId);
  return `<span data-result-value data-result-unit-key="${escapeHtml(key)}"
    data-result-source-unit="${model.canonicalUnit}"
    data-result-canonical-value="${escapeHtml(value)}">${escapeHtml(displayed)}</span>`;
}

function renderScalarResultRow(row, calculator, sectionIndex, tableIndex,
  rowIndex, unitProfile, unitOverrides) {
  if (row.header || row.cells.length < 3) return null;
  const [valueLabel, value, unitLabel] = row.cells;
  const numeric = resultNumber(value);
  const key = resultUnitKey(sectionIndex, tableIndex, `r${rowIndex}`);
  const model = resultUnitModel(calculator, unitLabel, valueLabel,
    unitProfile, unitOverrides, key);
  if (!model || numeric === null) return null;

  return `<tr><th scope="row">${escapeHtml(valueLabel)}</th>
    <td>${renderConvertedResultValue(numeric, model, key)}</td>
    <td class="result-unit-cell">${renderResultUnitSelect(model, key, valueLabel)}</td></tr>`;
}

function renderTrajectoryEnergyDivider(marker, columnModels, columnCount) {
  const rangeColumn = columnModels[marker.rangeColumnIndex];
  const thresholdColumn = columnModels[marker.energyColumnIndex];
  if (!rangeColumn?.model || !thresholdColumn?.model) return "";
  const thresholdValue = marker.thresholdEnergy;
  const rangeUnit = getUnitLabel(rangeColumn.model.unitId);
  const thresholdUnit = getUnitLabel(thresholdColumn.model.unitId);
  return `
    <tr class="trajectory-marker-table-divider energy-table-divider"
      data-energy-table-divider="${escapeHtml(marker.thresholdEnergy)}">
      <td colspan="${Math.max(1, columnCount)}">
        <span>Energy threshold ·
          ${renderConvertedResultValue(thresholdValue, thresholdColumn.model, thresholdColumn.key)}
          <span data-result-unit-label data-result-unit-key="${escapeHtml(thresholdColumn.key)}">${escapeHtml(thresholdUnit)}</span>
          at approximately
          ${renderConvertedResultValue(marker.range, rangeColumn.model, rangeColumn.key)}
          <span data-result-unit-label data-result-unit-key="${escapeHtml(rangeColumn.key)}">${escapeHtml(rangeUnit)}</span>
        </span>
      </td>
    </tr>`;
}

function renderTrajectoryTransonicBoundary(region, boundaryName,
  columnModels, columnCount) {
  const boundary = region[boundaryName];
  const rangeColumn = columnModels[region.rangeColumnIndex];
  if (!rangeColumn?.model) return "";
  const rangeUnit = getUnitLabel(rangeColumn.model.unitId);
  const rangeValue = renderConvertedResultValue(
    boundary.range, rangeColumn.model, rangeColumn.key);
  const rangeLabel = `<span data-result-unit-label data-result-unit-key="${escapeHtml(
    rangeColumn.key,
  )}">${escapeHtml(rangeUnit)}</span>`;
  let description;
  if (boundary.reached) {
    description = `${boundaryName === "entry" ? "Enters" : "Leaves"} transonic region · Mach ${escapeHtml(
      boundary.mach,
    )} at approximately ${rangeValue} ${rangeLabel}`;
  } else if (boundaryName === "entry") {
    description = `Calculated trajectory begins inside the transonic region (Mach 1.2–1.0) at ${rangeValue} ${rangeLabel}`;
  } else {
    description = `Mach 1.0 exit not reached · transonic region continues through the last calculated range, ${rangeValue} ${rangeLabel}`;
  }
  return `
    <tr class="trajectory-marker-table-divider transonic-region-table-boundary"
      data-transonic-region-boundary="${boundaryName}"
      data-transonic-boundary-reached="${boundary.reached}">
      <td colspan="${Math.max(1, columnCount)}"><span>${description}</span></td>
    </tr>`;
}

function renderResultTable(table, calculator, sectionIndex, tableIndex,
  unitProfile, unitOverrides, trajectoryIndicators = {
    transonicRegion: null, energyMarkers: [],
  }, { transonicTarget = null } = {}) {
  const firstHeaderIndex = table.rows.findIndex((row) => row.header);
  const unitHeaderIndex = firstHeaderIndex >= 0
    && table.rows[firstHeaderIndex + 1]?.header ? firstHeaderIndex + 1 : -1;
  const labels = firstHeaderIndex >= 0 ? table.rows[firstHeaderIndex].cells : [];
  const unitCells = unitHeaderIndex >= 0 ? table.rows[unitHeaderIndex].cells : [];
  const columnModels = labels.map((label, columnIndex) => {
    const key = resultUnitKey(sectionIndex, tableIndex, `c${columnIndex}`);
    return {
      key,
      label,
      model: resultUnitModel(calculator, unitCells[columnIndex], label,
        unitProfile, unitOverrides, key),
    };
  });
  const tableMarkers = trajectoryIndicators.energyMarkers.filter((marker) => (
    marker.sectionIndex === sectionIndex && marker.tableIndex === tableIndex
  ));
  const tableTransonicRegion = trajectoryIndicators.transonicRegion?.sectionIndex === sectionIndex
    && trajectoryIndicators.transonicRegion?.tableIndex === tableIndex
    ? trajectoryIndicators.transonicRegion : null;
  const transonicRows = new Set(tableTransonicRegion?.coveredRowIndices || []);
  const visibleColumnIndices = visibleResultColumnIndices(table.rows);
  const visibleColumns = new Set(visibleColumnIndices);

  const rows = table.rows.map((row, rowIndex) => {
    const energyDividers = tableMarkers
      .filter((marker) => marker.followingRowIndex === rowIndex)
      .map((marker) => renderTrajectoryEnergyDivider(
        marker, columnModels, visibleColumnIndices.length)).join("");
    const transonicDividers = tableTransonicRegion ? [
      tableTransonicRegion.entry.followingRowIndex === rowIndex
        ? renderTrajectoryTransonicBoundary(tableTransonicRegion,
          "entry", columnModels, visibleColumnIndices.length) : "",
      tableTransonicRegion.exit.reached
        && tableTransonicRegion.exit.followingRowIndex === rowIndex
        ? renderTrajectoryTransonicBoundary(tableTransonicRegion,
          "exit", columnModels, visibleColumnIndices.length) : "",
    ].join("") : "";
    const divider = `${energyDividers}${transonicDividers}`;
    const scalar = renderScalarResultRow(row, calculator, sectionIndex,
      tableIndex, rowIndex, unitProfile, unitOverrides);
    if (scalar) return divider + scalar;

    if (rowIndex === unitHeaderIndex) {
      return `${divider}<tr>${row.cells.map((cell, columnIndex) => {
        if (!visibleColumns.has(columnIndex)) return "";
        const column = columnModels[columnIndex];
        return `<th>${column?.model
          ? renderResultUnitSelect(column.model, column.key, column.label)
          : escapeHtml(cell)}</th>`;
      }).join("")}</tr>`;
    }

    const inTransonicRegion = transonicRows.has(rowIndex) || (
      transonicTarget && !row.header
    );
    const transonicAttributes = inTransonicRegion
      ? ` class="trajectory-transonic-region-row" data-transonic-region-row
        title="${escapeHtml(transonicTarget
    ? `Target is inside the transonic region (Mach ${transonicTarget.mach}; region Mach 1.2–1.0).`
    : "This range interval intersects the transonic region (Mach 1.2–1.0).")}"`
      : "";
    const trailingBoundary = tableTransonicRegion?.endsInsideRegion
      && tableTransonicRegion.lastRowIndex === rowIndex
      ? renderTrajectoryTransonicBoundary(tableTransonicRegion,
        "exit", columnModels, visibleColumnIndices.length) : "";
    return `${divider}<tr${transonicAttributes}>${row.cells.map((cell, columnIndex) => {
      if (!visibleColumns.has(columnIndex)) return "";
      const column = columnModels[columnIndex];
      const numeric = resultNumber(cell);
      const content = !row.header && column?.model && numeric !== null
        ? renderConvertedResultValue(numeric, column.model, column.key)
        : escapeHtml(cell);
      if (row.header) return `<th scope="col">${content}</th>`;
      return columnIndex === visibleColumnIndices[0]
        ? `<th scope="row">${content}</th>`
        : `<td>${content}</td>`;
    }).join("")}</tr>${trailingBoundary}`;
  }).join("");

  return `<div class="table-responsive"><table class="table table-sm table-hover align-middle">${rows}</table></div>`;
}

function renderTrajectoryChart(result, calculator, unitProfile, unitOverrides,
  suppliedIndicators = null, { expanded = true } = {}) {
  if (calculator.module !== "trajectory") return "";
  const series = extractTrajectorySeries(result);
  if (!series) return "";

  const rangeKey = resultUnitKey(series.sectionIndex, series.tableIndex,
    `c${series.rangeColumnIndex}`);
  const dropKey = resultUnitKey(series.sectionIndex, series.tableIndex,
    `c${series.dropColumnIndex}`);
  const velocityKey = resultUnitKey(series.sectionIndex, series.tableIndex,
    `c${series.velocityColumnIndex}`);
  const energyKey = resultUnitKey(series.sectionIndex, series.tableIndex,
    `c${series.energyColumnIndex}`);
  const rangeModel = resultUnitModel(calculator, series.rangeUnit, "Range",
    unitProfile, unitOverrides, rangeKey);
  const dropModel = resultUnitModel(calculator, series.dropUnit, "Drop",
    unitProfile, unitOverrides, dropKey);
  const velocityModel = series.velocityColumnIndex >= 0
    ? resultUnitModel(calculator, series.velocityUnit, "Velocity",
      unitProfile, unitOverrides, velocityKey) : null;
  const energyModel = series.energyColumnIndex >= 0
    ? resultUnitModel(calculator, series.energyUnit, "Energy",
      unitProfile, unitOverrides, energyKey) : null;
  if (!rangeModel || !dropModel) return "";

  const chartScale = trajectoryChartScalePoints(series.points, {
    angular: dropModel.correlation === "correctionAngle",
    zeroRangeYards: result.metadata?.zeroRangeYards,
  });
  const plottedSeriesPoints = decimateTrajectoryChartPoints(series.points);
  const points = plottedSeriesPoints.map((point) => ({
    x: convertUnitValue(point.range, rangeModel.canonicalUnit, rangeModel.unitId),
    y: convertUnitValue(point.drop, dropModel.canonicalUnit, dropModel.unitId),
  }));
  const scalePoints = chartScale.points.map((point) => ({
    x: convertUnitValue(point.range, rangeModel.canonicalUnit, rangeModel.unitId),
    y: convertUnitValue(point.drop, dropModel.canonicalUnit, dropModel.unitId),
  }));
  const geometry = buildTrajectoryChartGeometry(points, { yDomainPoints: scalePoints });
  if (!geometry) return "";

  const indicators = suppliedIndicators || extractTrajectoryIndicators(result, series);
  const markers = indicators.energyMarkers;
  const transonicRegion = indicators.transonicRegion;
  const rangeUnit = getUnitLabel(rangeModel.unitId);
  const dropUnit = getUnitLabel(dropModel.unitId);
  const markerDetails = markers.flatMap((marker) => {
    if (!energyModel) return [];
    const thresholdUnit = getUnitLabel(energyModel.unitId);
    const displayedThreshold = convertUnitValue(
      marker.thresholdEnergy, energyModel.canonicalUnit, energyModel.unitId);
    const displayedRange = convertUnitValue(
      marker.range, rangeModel.canonicalUnit, rangeModel.unitId);
    const x = chartXPosition(geometry, displayedRange);
    if (x === null) return [];
    return [{
      marker,
      displayedRange,
      x,
      label: `Energy threshold: ${formatUnitValue(displayedThreshold, energyModel.unitId)} ${thresholdUnit} at ${formatUnitValue(displayedRange, rangeModel.unitId)} ${rangeUnit}`,
    }];
  });
  const displayedRegion = transonicRegion ? (() => {
    const boundary = (source) => {
      const displayedRange = convertUnitValue(
        source.range, rangeModel.canonicalUnit, rangeModel.unitId);
      const clippedRange = Math.min(geometry.xMax, Math.max(geometry.xMin, displayedRange));
      return {
        ...source,
        displayedRange,
        clippedToPlot: clippedRange !== displayedRange,
        x: chartXPosition(geometry, clippedRange),
      };
    };
    const entry = boundary(transonicRegion.entry);
    const exitBoundary = boundary(transonicRegion.exit);
    if (entry.x === null || exitBoundary.x === null) return null;
    const entryText = entry.reached
      ? `Enters transonic region: Mach 1.2 at approximately ${formatUnitValue(
        entry.displayedRange, rangeModel.unitId,
      )} ${rangeUnit}`
      : `Calculated trajectory begins inside the transonic region at ${formatUnitValue(
        entry.displayedRange, rangeModel.unitId,
      )} ${rangeUnit}`;
    const exitText = exitBoundary.reached
      ? `Leaves transonic region: Mach 1.0 at approximately ${formatUnitValue(
        exitBoundary.displayedRange, rangeModel.unitId,
      )} ${rangeUnit}`
      : `Mach 1.0 exit not reached; transonic region continues through ${formatUnitValue(
        exitBoundary.displayedRange, rangeModel.unitId,
      )} ${rangeUnit}`;
    return { entry, exit: exitBoundary, entryText, exitText };
  })() : null;
  const clipId = `trajectory-clip-${calculator.slug}`;
  const xGrid = geometry.xTicks.map((tick) => `
    <line class="chart-grid-line" x1="${tick.position}" y1="${geometry.padding.top}"
      x2="${tick.position}" y2="${geometry.height - geometry.padding.bottom}"></line>
    <text class="chart-tick-label" x="${tick.position}" y="${geometry.height - geometry.padding.bottom + 22}"
      text-anchor="middle">${escapeHtml(formatChartTick(tick.value))}</text>`).join("");
  const yGrid = geometry.yTicks.map((tick) => `
    <line class="chart-grid-line" x1="${geometry.padding.left}" y1="${tick.position}"
      x2="${geometry.width - geometry.padding.right}" y2="${tick.position}"></line>
    <text class="chart-tick-label" x="${geometry.padding.left - 12}" y="${tick.position + 4}"
      text-anchor="end">${escapeHtml(formatChartTick(tick.value))}</text>`).join("");
  const transonicSvg = displayedRegion ? (() => {
    const { entry, exit: exitBoundary } = displayedRegion;
    const x = Math.min(entry.x, exitBoundary.x);
    const width = Math.max(0, Math.abs(exitBoundary.x - entry.x));
    const boundaryLine = (boundary, name, label, labelRow) => {
      if (!boundary.reached) return "";
      const textAnchor = boundary.x > geometry.width - 270 ? "end" : "start";
      const labelX = boundary.x + (textAnchor === "end" ? -8 : 8);
      return `<line class="chart-transonic-boundary-line"
          data-transonic-region-boundary="${name}" x1="${boundary.x}"
          y1="${geometry.padding.top}" x2="${boundary.x}"
          y2="${geometry.height - geometry.padding.bottom}"></line>
        <text class="chart-threshold-label chart-transonic-label" x="${labelX}"
          y="${geometry.padding.top + 38 + labelRow * 19}"
          text-anchor="${textAnchor}">${escapeHtml(label)}</text>`;
    };
    return `<rect class="chart-transonic-region" data-transonic-region
        x="${x}" y="${geometry.padding.top}" width="${width}"
        height="${geometry.height - geometry.padding.top - geometry.padding.bottom}"
        clip-path="url(#${clipId})"></rect>
      <text class="chart-transonic-region-label" x="${x + width / 2}"
        y="${geometry.padding.top + 17}" text-anchor="middle">Transonic region · Mach 1.2–1.0</text>
      ${boundaryLine(entry, "entry", `Mach 1.2 entry · ${formatUnitValue(
    entry.displayedRange, rangeModel.unitId,
  )} ${rangeUnit}`, 0)}
      ${boundaryLine(exitBoundary, "exit", `Mach 1.0 exit · ${formatUnitValue(
    exitBoundary.displayedRange, rangeModel.unitId,
  )} ${rangeUnit}`, 1)}`;
  })() : "";
  const markerSvg = markerDetails.map(({ marker, x, label }, index) => {
    const textAnchor = x > geometry.width - 250 ? "end" : "start";
    const labelX = x + (textAnchor === "end" ? -8 : 8);
    return `
      <line class="chart-threshold-line chart-energy-line"
        data-energy-threshold-line="${escapeHtml(marker.thresholdEnergy)}" x1="${x}"
        y1="${geometry.padding.top}" x2="${x}"
        y2="${geometry.height - geometry.padding.bottom}"></line>
      <text class="chart-threshold-label chart-energy-label" x="${labelX}"
        y="${geometry.padding.top + 76 + index * 19}"
        text-anchor="${textAnchor}">${escapeHtml(label)}</text>`;
  }).join("");
  const markerData = [
    ...markerDetails.map(({ marker, displayedRange }) => (
      `data-energy-${marker.thresholdEnergy}-range="${escapeHtml(displayedRange)}"`
    )),
    ...(displayedRegion ? [
      `data-transonic-entry-range="${escapeHtml(displayedRegion.entry.displayedRange)}"`,
      `data-transonic-exit-range="${escapeHtml(displayedRegion.exit.displayedRange)}"`,
      `data-transonic-entry-reached="${displayedRegion.entry.reached}"`,
      `data-transonic-exit-reached="${displayedRegion.exit.reached}"`,
    ] : []),
  ].join(" ");
  const markerDescription = [
    ...(displayedRegion
      ? [` ${displayedRegion.entryText}. ${displayedRegion.exitText}.`] : []),
    ...markerDetails.map(({ label }) => ` ${label}.`),
  ].join("");
  const displayedZeroRange = chartScale.postZeroScale
    ? convertUnitValue(chartScale.zeroRangeYards,
      rangeModel.canonicalUnit, rangeModel.unitId) : null;
  const zeroRangeX = displayedZeroRange === null
    ? null : chartXPosition(geometry, displayedZeroRange);
  const formattedZeroRange = displayedZeroRange === null
    ? "" : formatUnitValue(displayedZeroRange, rangeModel.unitId);
  const zeroRangeLabel = displayedZeroRange === null
    ? "" : `Established zero: ${formattedZeroRange} ${rangeUnit}`;
  const zeroRangeTextAnchor = zeroRangeX !== null && zeroRangeX > geometry.width - 180
    ? "end" : "start";
  const zeroRangeLabelX = zeroRangeX === null
    ? null : zeroRangeX + (zeroRangeTextAnchor === "end" ? -8 : 8);
  const zeroRangeMarker = zeroRangeX === null ? "" : `
    <line class="chart-zero-range-line" data-chart-zero-range
      x1="${zeroRangeX}" y1="${geometry.padding.top}"
      x2="${zeroRangeX}" y2="${geometry.height - geometry.padding.bottom}"></line>
    <text class="chart-zero-range-label" x="${zeroRangeLabelX}"
      y="${geometry.height - geometry.padding.bottom - 9}"
      text-anchor="${zeroRangeTextAnchor}">${escapeHtml(zeroRangeLabel)}</text>`;
  const angularFocusNote = chartScale.postZeroScale
    && chartScale.omittedPointCount > 0
    ? `<p class="trajectory-chart-focus-note" data-trajectory-chart-focus-note>
      <i class="bi bi-info-circle" aria-hidden="true"></i>
      Angular scale emphasizes drop at and beyond the established ${escapeHtml(
    formattedZeroRange,
  )} ${escapeHtml(rangeUnit)} zero. The earlier trajectory remains on the graph, but extreme close-range angular values may be clipped so they do not flatten the useful downrange detail. Select Linear output and recalculate for an unclipped pre-zero physical path.
    </p>` : "";

  return `
    <section class="trajectory-chart-card" data-trajectory-chart-host
      data-range-unit="${escapeHtml(rangeModel.unitId)}"
      data-drop-unit="${escapeHtml(dropModel.unitId)}"
      data-velocity-unit="${escapeHtml(velocityModel?.unitId || "")}"
      data-energy-unit="${escapeHtml(energyModel?.unitId || "")}"
      data-source-samples="${series.points.length}"
      data-plotted-samples="${points.length}"
      data-angular-post-zero-scale="${chartScale.postZeroScale}"
      ${chartScale.postZeroScale
    ? `data-zero-range-yards="${escapeHtml(chartScale.zeroRangeYards)}"
      data-scale-start-yards="${escapeHtml(chartScale.points[0].range)}"` : ""}
      ${markerData}>
      <details class="trajectory-chart-details"${expanded ? " open" : ""}>
        <summary class="trajectory-chart-header"
          aria-labelledby="trajectory-chart-heading-${calculator.slug}">
          <span class="trajectory-chart-title-group">
            <span class="trajectory-chart-title" id="trajectory-chart-heading-${calculator.slug}"
              role="heading" aria-level="3">Bullet trajectory</span>
            <span class="trajectory-chart-description">Drop relative to the line of sight across the calculated range.</span>
          </span>
          <span class="trajectory-chart-summary-meta">
            <span class="trajectory-chart-sample-count">${points.length} plotted samples</span>
            <i class="bi bi-chevron-down trajectory-chart-chevron" aria-hidden="true"></i>
          </span>
        </summary>
        ${angularFocusNote}
        <div class="trajectory-chart-viewport">
      <svg class="trajectory-chart" viewBox="0 0 ${geometry.width} ${geometry.height}"
        role="img" aria-labelledby="trajectory-chart-title-${calculator.slug} trajectory-chart-description-${calculator.slug}">
        <title id="trajectory-chart-title-${calculator.slug}">Bullet trajectory chart</title>
        <desc id="trajectory-chart-description-${calculator.slug}">Bullet drop in ${escapeHtml(dropUnit)} by range in ${escapeHtml(rangeUnit)}.${chartScale.postZeroScale ? ` Angular scale emphasizes values at and beyond the ${escapeHtml(formattedZeroRange)} ${escapeHtml(rangeUnit)} zero; earlier extreme values may be clipped.` : ""}${escapeHtml(markerDescription)}</desc>
        <defs><clipPath id="${clipId}"><rect x="${geometry.padding.left}" y="${geometry.padding.top}"
          width="${geometry.width - geometry.padding.left - geometry.padding.right}"
          height="${geometry.height - geometry.padding.top - geometry.padding.bottom}"></rect></clipPath></defs>
        ${transonicSvg}${xGrid}${yGrid}
        <line class="chart-zero-line" x1="${geometry.padding.left}" y1="${geometry.zeroY}"
          x2="${geometry.width - geometry.padding.right}" y2="${geometry.zeroY}"></line>
        ${zeroRangeMarker}
        ${markerSvg}
        <path class="trajectory-chart-line" d="${geometry.path}" clip-path="url(#${clipId})"></path>
        <text class="chart-axis-label" x="${geometry.width / 2}" y="${geometry.height - 8}"
          text-anchor="middle">Range (${escapeHtml(rangeUnit)})</text>
        <text class="chart-axis-label" transform="rotate(-90)" x="${-geometry.height / 2}" y="18"
          text-anchor="middle">Drop (${escapeHtml(dropUnit)})</text>
      </svg>
        ${displayedRegion ? `<p class="trajectory-transonic-summary"
          data-transonic-region-summary role="status">
          <i class="bi bi-exclamation-diamond-fill" aria-hidden="true"></i>
          <span><strong>Transonic region (Mach 1.2–1.0).</strong>
            ${escapeHtml(displayedRegion.entryText)}.
            ${escapeHtml(displayedRegion.exitText)}.</span>
        </p>` : ""}
        </div>
      </details>
    </section>`;
}

function refreshTrajectoryChart(calculator, container) {
  const current = container.querySelector("[data-trajectory-chart-host]");
  const result = resultCache.get(calculator.slug);
  if (!current || !result) return;
  const { unitProfile } = loadSettings();
  const expanded = current.querySelector(".trajectory-chart-details")?.open ?? true;
  const chartResult = result.chartResult?.error ? result : (result.chartResult || result);
  const replacement = renderTrajectoryChart(
    chartResult, calculator, unitProfile, loadUnitOverrides(), null, { expanded });
  if (replacement) current.outerHTML = replacement;
}

function changeResultUnit(calculator, container, selector) {
  const key = selector.dataset.resultUnitKey;
  const correlation = selector.dataset.resultUnitCorrelation;
  const nextUnit = selector.value;
  if (!key || !correlation || !nextUnit) return;

  container.querySelectorAll("[data-result-value]").forEach((value) => {
    if (value.dataset.resultUnitKey !== key) return;
    value.textContent = formatUnitValue(convertUnitValue(
      value.dataset.resultCanonicalValue,
      value.dataset.resultSourceUnit,
      nextUnit,
    ), nextUnit);
  });
  container.querySelectorAll("[data-result-unit-select]").forEach((select) => {
    if (select.dataset.resultUnitKey === key) select.value = nextUnit;
  });
  container.querySelectorAll("[data-result-unit-label]").forEach((label) => {
    if (label.dataset.resultUnitKey === key) label.textContent = getUnitLabel(nextUnit);
  });
  setUnitOverride(calculator.slug, `output.${correlation}.${key}`, nextUnit);
  const result = resultCache.get(calculator.slug);
  if (result?.kind === "range-table") {
    renderRangeTableResult(result, container, calculator, { scroll: false });
    return;
  }
  refreshTrajectoryChart(calculator, container);
}

function renderResultSection(section, calculator, unitProfile, unitOverrides,
  trajectoryIndicators = { transonicRegion: null, energyMarkers: [] },
  options = {}) {
  const tables = section.tables.map((table, tableIndex) => renderResultTable(
    table, calculator, section.resultIndex, tableIndex,
    unitProfile, unitOverrides, trajectoryIndicators, options)).join("");
  if (section.title === "Input Data") {
    return `
      <section class="result-section result-input" data-result-input>
        <details>
          <summary><span class="result-section-title">${escapeHtml(section.title)}</span><i class="bi bi-chevron-down" aria-hidden="true"></i></summary>
          <div class="result-section-body">${tables}</div>
        </details>
      </section>`;
  }
  if (section.title === "Trajectory Range Notice") {
    return `
      <section class="result-section trajectory-range-notice" role="status">
        <h3><i class="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>${escapeHtml(section.title)}</h3>
        ${tables}
      </section>`;
  }
  if (section.title === "Zero Solution") {
    const solutionId = `zero-solution-${calculator.slug}`;
    const helpId = `zero-solution-help-${calculator.slug}`;
    const helpTitleId = `${helpId}-title`;
    return `
      <section class="result-section zero-solution" data-zero-solution>
        <header class="zero-solution-header">
          <h3>
            <button class="zero-solution-toggle" type="button" data-bs-toggle="collapse"
              data-bs-target="#${solutionId}" aria-expanded="true" aria-controls="${solutionId}">
              <span><i class="bi bi-crosshair me-2" aria-hidden="true"></i>Zero solution</span>
              <i class="bi bi-chevron-down zero-solution-chevron" aria-hidden="true"></i>
            </button>
          </h3>
          <button class="btn btn-sm zero-solution-help-button" type="button"
            data-zero-solution-help data-bs-toggle="modal" data-bs-target="#${helpId}"
            aria-label="Explain the zero solution and its fields">?</button>
        </header>
        <div class="collapse show zero-solution-body" id="${solutionId}" data-zero-solution-body>
          <p class="zero-solution-description">Calculated initial corrections required for the projectile to intersect the selected zero point.</p>
          ${tables}
        </div>
      </section>
      <div class="modal fade zero-solution-help-modal" id="${helpId}" tabindex="-1"
        aria-labelledby="${helpTitleId}" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div class="modal-content">
            <div class="modal-header">
              <h3 class="modal-title fs-5" id="${helpTitleId}">Understanding the zero solution</h3>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body zero-solution-help-content">
              <p><strong>The zero solution is the modeled launch direction.</strong> It is the initial bore angle, relative to the line of sight, that places the projectile at the requested zero point after drag, gravity, wind, sight geometry, shot inclination angle, and cant are applied.</p>
              <p>It is not another trajectory-table sample. The table's Drop and Windage columns report projectile position at each listed range; the zero solution sets the initial direction used to calculate all of those rows.</p>

              <h4>Calculated fields</h4>
              <dl>
                <dt>Elevation correction</dt>
                <dd>The vertical launch angle solved at the exact zero range. A positive value points the modeled muzzle direction upward relative to the line of sight. ABC always calculates this correction.</dd>
                <dt>Azimuth correction</dt>
                <dd>The horizontal launch angle solved so the projectile reaches the requested lateral zero position. ABC always calculates this correction. Its sign follows the trajectory engine's lateral coordinate convention.</dd>
              </dl>

              <h4>Inputs that define the solution</h4>
              <dl>
                <dt>Zero range</dt>
                <dd>The distance along the line of sight where the selected vertical and horizontal zero constraints are applied. The solver evaluates this exact distance even when it falls between output-table intervals.</dd>
                <dt>Zero shift elevation</dt>
                <dd>The desired vertical projectile position relative to the line of sight at zero range. Zero means the projectile center intersects the line of sight; positive and negative values request a point above or below it.</dd>
                <dt>Zero shift windage</dt>
                <dd>The desired lateral projectile position relative to the line of sight at zero range. Zero requests a centered intersection; a nonzero value requests a deliberate horizontal offset.</dd>
                <dt>Sight height and sight offset</dt>
                <dd>The vertical and lateral separation between the sight line and bore at the muzzle. These establish the projectile's starting position relative to the line of sight.</dd>
                <dt>Post-zero dialed adjustments</dt>
                <dd><em>Dialed elevation</em> and <em>Dialed windage</em> describe corrections applied after the established zero. They remain shot-setting information while ABC always calculates the automatic zero solution for both axes.</dd>
              </dl>

              <h4>Precision and units</h4>
              <p>The original trajectory engine iterates the enabled axes up to ten times and accepts the zero when their combined positional miss is no more than 0.02 inch. Display units can be changed without rerunning the calculation. Corrections default to true milliradians (mrad); the engine's historical 1/6400-turn unit remains available as NATO mil.</p>

              <div class="alert alert-secondary mb-0" role="note">
                <strong>Practical interpretation:</strong> these angles describe the modeled bore-to-line-of-sight relationship. They may correspond to a sight correction, but they are not automatically identical to turret clicks because mounting geometry, adjustment direction, click resolution, and sight conventions can differ.
              </div>
            </div>
          </div>
        </div>
      </div>`;
  }
  const transonicTargetStatus = options.transonicTarget ? `
    <p class="target-transonic-region-status" data-target-transonic-region role="status">
      <i class="bi bi-exclamation-diamond-fill" aria-hidden="true"></i>
      Target is inside the transonic region (Mach ${escapeHtml(
    Number(options.transonicTarget.mach).toFixed(3),
  )}; region Mach 1.2–1.0).
    </p>` : "";
  return `
    <section class="result-section${options.transonicTarget
    ? " target-solution-transonic" : ""}">
      <h3>${escapeHtml(section.title)}</h3>
      ${transonicTargetStatus}
      ${tables}
    </section>`;
}

function selectedInputUnit(calculator, fieldName, unitProfile, unitOverrides) {
  if (fieldName === "mv"
    && calculatorFields(calculator).some(({ name }) => name === "mvtb")) {
    const field = { type: "number", name: "mvtb", label: "Muzzle velocity", unit: "ft/s" };
    return {
      field,
      unitId: getMvTemperatureTableUnits(
        unitProfile,
        null,
        unitOverrides[`${calculator.requiresGun ? "gun" : calculator.slug}.mvtb.velocity`],
      ).velocityUnit,
    };
  }
  const field = findCalculatorField(calculator, fieldName);
  if (!field) return { field: null, unitId: null };
  return {
    field,
    unitId: getFieldUnit(field, unitProfile,
      unitOverrides[fieldUnitOverrideKey(calculator, fieldName)]),
  };
}

function convertedResultText(value, model) {
  if (!model || value === null || value === undefined || !Number.isFinite(Number(value))) {
    return "—";
  }
  return formatUnitValue(convertUnitValue(
    value, model.canonicalUnit, model.unitId), model.unitId);
}

function renderResultGunStatus(calculator) {
  if (!calculator.requiresGun) return "";
  const calculated = resultGunProvenance.get(calculator.slug);
  if (!calculated) return "";
  const currentStatus = gunLibrary.getStatus();
  const currentFingerprint = gunLibrary.getDraftFingerprint();
  const stale = !currentStatus.configured
    || calculated.fingerprint !== currentFingerprint;
  if (!stale) {
    return `<div class="result-gun-status result-gun-status-current" data-result-gun-state="current">
      <i class="bi bi-check-circle-fill" aria-hidden="true"></i>
      Calculated with <strong>${escapeHtml(calculated.label)}</strong>.
    </div>`;
  }
  return `<div class="alert alert-warning result-gun-status result-gun-status-stale"
      data-result-gun-state="stale" role="status">
    <i class="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
    <span><strong>Out of date.</strong> These results used ${escapeHtml(calculated.label)};
      the current gun is ${escapeHtml(gunStatusLabel(currentStatus))}. Recalculate before using them.</span>
  </div>`;
}

function solutionTargetSection(result) {
  const series = extractTrajectorySeries(result);
  if (!series) return null;
  const target = Number(result.metadata?.targetRangeYards);
  if (!Number.isFinite(target)) return null;
  const sample = findSolutionTargetSample(series.samples, target);
  if (!sample) return null;
  const sourceSection = result.sections[series.sectionIndex];
  const sourceTable = sourceSection?.tables?.[series.tableIndex];
  if (!sourceTable) return null;
  const firstHeaderIndex = sourceTable.rows.findIndex((row) => row.header);
  const unitHeaderIndex = firstHeaderIndex >= 0
    && sourceTable.rows[firstHeaderIndex + 1]?.header ? firstHeaderIndex + 1 : -1;
  const headerRows = sourceTable.rows.slice(
    firstHeaderIndex, (unitHeaderIndex >= 0 ? unitHeaderIndex : firstHeaderIndex) + 1);
  const indicators = extractTrajectoryIndicators(result, series);
  const region = indicators.transonicRegion;
  const transonicTarget = region && sample.mach <= region.entry.mach
    && sample.mach >= region.exit.mach
    ? { mach: sample.mach } : null;
  return {
    title: "Target Solution",
    resultIndex: series.sectionIndex,
    tables: [{ rows: [...headerRows, sourceTable.rows[sample.rowIndex]] }],
    transonicTarget,
  };
}

function compactSvgNumber(value) {
  return String(Number(Number(value).toFixed(5)));
}

function svgPolygonPoints(points) {
  return points.map(({ x, y }) => (
    `${compactSvgNumber(x)},${compactSvgNumber(y)}`
  )).join(" ");
}

function renderTargetScoringOverlay(target) {
  const overlay = engagementZoneScoringOverlayGeometry(target);
  if (!overlay) return "";
  const boundaries = overlay.zoneBoundaries.map(({ id, points }) => (
    `<polygon class="engagement-target-zone engagement-target-zone-${id}"
      data-target-zone="${id}" points="${svgPolygonPoints(points)}"></polygon>`
  )).join("");
  const labels = overlay.zoneLabels.map(({ zone, x, y }) => (
    `<text class="engagement-target-zone-label" data-target-zone-label="${zone}"
      x="${compactSvgNumber(x)}" y="${compactSvgNumber(y)}">${zone}</text>`
  )).join("");
  return `<g class="engagement-target-scoring" aria-hidden="true">
    <polygon class="engagement-target-scoring-border"
      points="${svgPolygonPoints(overlay.borderPolygon)}"></polygon>
    ${boundaries}${labels}
  </g>`;
}

function renderEngagementZone(analysis, calculator, unitProfile, unitOverrides) {
  if (!analysis) return "";
  if (analysis.error) {
    return `<section class="result-section engagement-zone-card" data-engagement-zone>
      <h3>Engagement Zone</h3>
      <div class="alert alert-warning mb-0" role="status">
        <strong>Simulation unavailable.</strong> ${escapeHtml(analysis.error)}
      </div>
    </section>`;
  }

  const geometry = engagementZonePlotGeometry(analysis);
  const targetPoints = analysis.target.polygonInches.map((point) => {
    const svgPoint = engagementZoneSvgPoint(point);
    return `${compactSvgNumber(svgPoint.x)},${compactSvgNumber(svgPoint.y)}`;
  }).join(" ");
  const targetScoringOverlay = renderTargetScoringOverlay(analysis.target);
  const renderedImpacts = analysis.impacts.flatMap((impact) => {
    if (!Number.isFinite(impact.xInches) || !Number.isFinite(impact.yInches)) return [];
    const point = engagementZoneSvgPoint(impact);
    return [`<circle class="engagement-impact ${impact.hit
      ? "engagement-impact-hit" : "engagement-impact-miss"}"
      cx="${compactSvgNumber(point.x)}" cy="${compactSvgNumber(point.y)}"
      r="${compactSvgNumber(geometry.markerRadius)}"></circle>`];
  }).join("");
  const widthSelection = selectedInputUnit(
    calculator, "ezTargetWidth", unitProfile, unitOverrides,
  );
  const heightSelection = selectedInputUnit(
    calculator, "ezTargetHeight", unitProfile, unitOverrides,
  );
  const widthUnit = widthSelection.unitId || "inch";
  const heightUnit = heightSelection.unitId || "inch";
  const widthUnitLabel = getUnitLabel(widthUnit);
  const heightUnitLabel = getUnitLabel(heightUnit);
  const dimensions = widthSelection.field && heightSelection.field
    ? `${fieldValueFromCanonical(
      widthSelection.field, analysis.target.widthInches, widthUnit,
    )}${widthUnit === heightUnit ? "" : ` ${widthUnitLabel}`} × ${fieldValueFromCanonical(
      heightSelection.field, analysis.target.heightInches, heightUnit,
    )} ${heightUnitLabel}`
    : `${formatUnitValue(analysis.target.widthInches, "inch")} × ${formatUnitValue(
      analysis.target.heightInches, "inch",
    )} in`;
  const viewBox = geometry.viewBox;
  const aimSize = Math.min(
    analysis.target.widthInches, analysis.target.heightInches,
  ) * 0.035;
  const unmodelable = analysis.unmodelableShotCount > 0
    ? `<span>${analysis.unmodelableShotCount} unmodeled trajectories counted as misses</span>`
    : "";

  return `<section class="result-section engagement-zone-card" data-engagement-zone
      data-shot-count="${analysis.shotCount}" data-hit-count="${analysis.hitCount}"
      data-miss-count="${analysis.missCount}"
      data-transonic-uncertainty="${analysis.transonicUncertaintyEnabled
    ? "enabled" : "disabled"}"
      data-transonic-severity="${analysis.transonicSeverityPercent}">
    <div class="engagement-zone-header">
      <div>
        <h3>Engagement Zone</h3>
        <p>${escapeHtml(analysis.target.label)} · ${escapeHtml(dimensions)}</p>
      </div>
      <div class="engagement-zone-probability" aria-label="${escapeHtml(
    `${formatUnitValue(analysis.hitPercentage, "percent")} percent estimated hit probability`,
  )}">
        <strong>${escapeHtml(formatUnitValue(
    analysis.hitPercentage, "percent",
  ))}%</strong>
        <span>estimated hits</span>
      </div>
    </div>
    <div class="engagement-zone-summary" role="status">
      <span><strong>${analysis.hitCount}</strong> hits</span>
      <span><strong>${analysis.missCount}</strong> misses</span>
      <span><strong>${analysis.shotCount.toLocaleString("en-US")}</strong> simulated shots</span>
      ${unmodelable}
    </div>
    <div class="engagement-zone-viewport">
      <svg class="engagement-zone-plot"
        viewBox="${compactSvgNumber(viewBox.x)} ${compactSvgNumber(viewBox.y)} ${compactSvgNumber(viewBox.width)} ${compactSvgNumber(viewBox.height)}"
        role="img" aria-labelledby="engagement-zone-title engagement-zone-description">
        <title id="engagement-zone-title">Engagement Zone impact plot</title>
        <desc id="engagement-zone-description">${escapeHtml(
    `${analysis.hitCount} hits and ${analysis.missCount} misses from ${analysis.shotCount} Monte Carlo shots against a centered ${analysis.target.label}.`,
  )}</desc>
        <polygon class="engagement-target" points="${targetPoints}"
          stroke-width="${compactSvgNumber(geometry.strokeWidth)}"></polygon>
        ${targetScoringOverlay}
        <g class="engagement-aim-point" aria-hidden="true"
          stroke-width="${compactSvgNumber(geometry.strokeWidth)}">
          <line x1="${compactSvgNumber(-aimSize)}" y1="0"
            x2="${compactSvgNumber(aimSize)}" y2="0"></line>
          <line x1="0" y1="${compactSvgNumber(-aimSize)}"
            x2="0" y2="${compactSvgNumber(aimSize)}"></line>
        </g>
        <g class="engagement-impacts" aria-hidden="true"
          stroke-width="${compactSvgNumber(geometry.strokeWidth)}">${renderedImpacts}</g>
      </svg>
    </div>
    <div class="engagement-zone-legend" aria-label="Impact marker legend">
      <span><i class="engagement-legend-dot engagement-legend-hit" aria-hidden="true"></i>Hit</span>
      <span><i class="engagement-legend-dot engagement-legend-miss" aria-hidden="true"></i>Miss</span>
    </div>
    <p class="engagement-zone-method">Uses a fixed nominal firing solution and ${analysis.shotCount.toLocaleString("en-US")} deterministic trials. Independent ± uncertainties represent 95% normal intervals; velocity ES is modeled as the full 95% span. Generic transonic uncertainty is ${analysis.transonicUncertaintyEnabled
    ? `enabled at ${formatUnitValue(analysis.transonicSeverityPercent, "percent")}% severity`
    : `disabled (effective severity ${formatUnitValue(analysis.transonicSeverityPercent, "percent")}%)`}.</p>
  </section>`;
}

function renderRangeTableCaseHeader(trajectoryCase, temperatureField,
  temperatureUnit, velocityField, velocityUnit) {
  const temperature = fieldValueFromCanonical(temperatureField,
    trajectoryCase.temperatureFahrenheit, temperatureUnit);
  const velocity = fieldValueFromCanonical(velocityField,
    trajectoryCase.muzzleVelocityFps, velocityUnit);
  return `<span class="range-table-temperature">${escapeHtml(temperature)} ${escapeHtml(getUnitLabel(temperatureUnit))}</span>
    <span class="range-table-mv">MV ${escapeHtml(velocity)} ${escapeHtml(getUnitLabel(velocityUnit))}</span>`;
}

function rangeTableMarkerTitle(marker, rangeModel) {
  const range = convertedResultText(marker.range, rangeModel);
  const rangeUnit = getUnitLabel(rangeModel.unitId);
  return `${marker.kind === "energy-500" ? "500" : "350"} ft·lbf retained energy at approximately ${range} ${rangeUnit}`;
}

function rangeTableTransonicTitle(status, rangeModel) {
  if (!status) return null;
  const { region } = status;
  const rangeUnit = getUnitLabel(rangeModel.unitId);
  const entryRange = convertedResultText(region.entry.range, rangeModel);
  const exitRange = convertedResultText(region.exit.range, rangeModel);
  const details = [];
  if (status.entryBoundary) {
    details.push(`Enters transonic region at Mach 1.2 near ${entryRange} ${rangeUnit}`);
  } else if (status.beginsInside) {
    details.push(`Calculated trajectory begins inside the transonic region at ${entryRange} ${rangeUnit}`);
  } else {
    details.push("Range interval intersects the transonic region (Mach 1.2–1.0)");
  }
  if (status.exitBoundary) {
    details.push(`leaves the region at Mach 1.0 near ${exitRange} ${rangeUnit}`);
  } else if (status.exitNotReached) {
    details.push(`Mach 1.0 exit not reached by ${exitRange} ${rangeUnit}`);
  }
  return details.join("; ");
}

function renderRangeTableDropCell(value, markers, transonicStatus,
  dropModel, rangeModel) {
  const titles = markers.map((marker) => rangeTableMarkerTitle(
    marker, rangeModel));
  const transonicTitle = rangeTableTransonicTitle(transonicStatus, rangeModel);
  if (transonicTitle) titles.unshift(transonicTitle);
  const classes = [
    ...markers.map(({ kind }) => `threshold-${kind}`),
    ...(transonicStatus ? ["range-table-transonic-region"] : []),
    ...(transonicStatus?.entryBoundary || transonicStatus?.beginsInside
      ? ["range-table-transonic-entry"] : []),
    ...(transonicStatus?.exitBoundary || transonicStatus?.exitNotReached
      ? ["range-table-transonic-exit"] : []),
  ].join(" ");
  const flags = markers.length > 0 ? `<span class="range-threshold-flags" aria-hidden="true">
    ${markers.map(({ kind }) => `<i class="threshold-flag threshold-flag-${kind}"></i>`).join("")}
  </span>` : "";
  return `<td class="range-table-value range-table-drop ${classes}"
      ${transonicStatus ? "data-transonic-region-cell" : ""}
      ${transonicStatus?.entryBoundary || transonicStatus?.beginsInside
    ? "data-transonic-region-entry" : ""}
      ${transonicStatus?.exitBoundary || transonicStatus?.exitNotReached
    ? `data-transonic-region-exit="${transonicStatus.exitNotReached
      ? "not-reached" : "reached"}"` : ""}
      ${titles.length > 0 ? `data-threshold-marker title="${escapeHtml(titles.join("; "))}"` : ""}>
    <span>${escapeHtml(convertedResultText(value, dropModel))}</span>${flags}
    ${titles.length > 0 ? `<span class="visually-hidden">${escapeHtml(titles.join("; "))}</span>` : ""}
  </td>`;
}

function renderRangeTableResult(result, container, calculator, { scroll = true } = {}) {
  const { unitProfile } = loadSettings();
  const unitOverrides = loadUnitOverrides();
  const sampledVisibility = rangeTableSampledColumnVisibility(result.sampledCases);
  const showWindage = sampledVisibility.windage;
  const showLead = sampledVisibility.lead;
  const rangeModel = resultUnitModel(calculator, result.sourceUnits.range,
    "Range", unitProfile, unitOverrides, "range-table.range");
  const dropModel = resultUnitModel(calculator, result.sourceUnits.drop,
    "Drop", unitProfile, unitOverrides, "range-table.drop");
  const windageModel = showWindage
    ? resultUnitModel(calculator, result.sourceUnits.windage,
      "Windage", unitProfile, unitOverrides, "range-table.windage") : null;
  const leadModel = showLead
    ? resultUnitModel(calculator, result.sourceUnits.lead,
      "Lead", unitProfile, unitOverrides, "range-table.lead") : null;
  const { field: temperatureField, unitId: temperatureUnit } = selectedInputUnit(
    calculator, "temps", unitProfile, unitOverrides);
  const { field: velocityField, unitId: velocityUnit } = selectedInputUnit(
    calculator, "mv", unitProfile, unitOverrides);
  const { field: targetSpeedField, unitId: targetSpeedUnit } = selectedInputUnit(
    calculator, "sp", unitProfile, unitOverrides);
  if (!rangeModel || !dropModel || (showWindage && !windageModel)
    || (showLead && !leadModel)
    || !temperatureField || !temperatureUnit || !velocityField || !velocityUnit
    || (showLead && (!targetSpeedField || !targetSpeedUnit))) {
    renderResult({ error: "The range table returned an unsupported unit layout.", sections: [] },
      container, calculator, { scroll });
    return;
  }

  const previousRange = (index) => index > 0 ? result.ranges[index - 1] : null;
  const rows = result.ranges.map((range, rowIndex) => `<tr>
    <th class="range-table-range" scope="row">${escapeHtml(convertedResultText(range, rangeModel))}</th>
    ${result.dropCases.map((trajectoryCase) => {
      const value = rangeTableValueAt(trajectoryCase, range)?.drop;
      const markers = rangeTableMarkersAt(
        trajectoryCase, range, previousRange(rowIndex));
      const transonicStatus = rangeTableTransonicRegionAt(
        trajectoryCase, range, previousRange(rowIndex));
      return renderRangeTableDropCell(value, markers, transonicStatus,
        dropModel, rangeModel);
    }).join("")}
    ${showWindage ? result.sampledCases.map((trajectoryCase) => `<td class="range-table-value range-table-wind">
      ${escapeHtml(convertedResultText(
        rangeTableValueAt(trajectoryCase, range)?.windage, windageModel))}
    </td>`).join("") : ""}
    ${showLead ? result.sampledCases.map((trajectoryCase) => `<td class="range-table-value range-table-lead">
      ${escapeHtml(convertedResultText(
        rangeTableValueAt(trajectoryCase, range)?.lead, leadModel))}
    </td>`).join("") : ""}
  </tr>`).join("");
  const notices = result.notices.length > 0 ? `
    <div class="alert alert-warning range-table-notice" role="status">
      <strong>Some temperature cases ended early.</strong>
      Blank cells remain beyond the last numerically safe sample.
      <ul class="mb-0 mt-2">${[...new Set(result.notices)]
    .map((notice) => `<li>${escapeHtml(notice)}</li>`).join("")}</ul>
    </div>` : "";
  const targetSpeed = showLead ? fieldValueFromCanonical(
    targetSpeedField, result.targetSpeedMph, targetSpeedUnit,
  ) : "0";
  const targetDirection = String(Number(result.targetDirectionDegrees));
  const lateralLeadLabel = showLead
    ? `${targetSpeed} ${getUnitLabel(targetSpeedUnit)} lateral lead · ${targetDirection}°`
    : "";
  const captionParts = [
    "Temperature-adjusted bullet drop",
    showWindage ? "total windage" : "",
    showLead ? lateralLeadLabel.toLowerCase() : "",
  ].filter(Boolean);
  const rangeTableCaption = `${captionParts.join(", ")} by range.`;

  container.innerHTML = `
    <div class="result-header"><h2>Results</h2></div>
    ${renderResultGunStatus(calculator)}
    ${notices}
    <section class="range-threshold-legend" aria-label="Threshold color legend">
      <span><i class="threshold-swatch threshold-swatch-transonic-region"></i>Transonic region (Mach 1.2–1.0)</span>
      <span><i class="threshold-swatch threshold-swatch-energy-500"></i>500 ft·lbf</span>
      <span><i class="threshold-swatch threshold-swatch-energy-350"></i>350 ft·lbf</span>
    </section>
    <section class="result-section range-table-card">
      <h3>Temperature-adjusted firing table</h3>
      <div class="table-responsive range-table-viewport">
        <table class="table table-sm align-middle range-table">
          <caption class="visually-hidden">${escapeHtml(rangeTableCaption)}</caption>
          <thead>
            <tr class="range-table-groups">
              <th class="range-table-range" rowspan="2" scope="col">Range<br>${renderResultUnitSelect(rangeModel, "range-table.range", "Range")}</th>
              <th class="drop-group" colspan="${result.dropCases.length}" scope="colgroup">Bullet drop ${renderResultUnitSelect(dropModel, "range-table.drop", "Drop")}</th>
              ${showWindage ? `<th class="wind-group" colspan="${result.sampledCases.length}" scope="colgroup">Total Windage ${renderResultUnitSelect(windageModel, "range-table.windage", "Windage")}</th>` : ""}
              ${showLead ? `<th class="lead-group" colspan="${result.sampledCases.length}" scope="colgroup">${escapeHtml(lateralLeadLabel)} ${renderResultUnitSelect(leadModel, "range-table.lead", "Lead")}</th>` : ""}
            </tr>
            <tr class="range-table-cases">
              ${result.dropCases.map((trajectoryCase) => `<th class="drop-group" scope="col">${renderRangeTableCaseHeader(
    trajectoryCase, temperatureField, temperatureUnit, velocityField, velocityUnit)}</th>`).join("")}
              ${showWindage ? result.sampledCases.map((trajectoryCase) => `<th class="wind-group" scope="col">${renderRangeTableCaseHeader(
    trajectoryCase, temperatureField, temperatureUnit, velocityField, velocityUnit)}</th>`).join("") : ""}
              ${showLead ? result.sampledCases.map((trajectoryCase) => `<th class="lead-group" scope="col">${renderRangeTableCaseHeader(
    trajectoryCase, temperatureField, temperatureUnit, velocityField, velocityUnit)}</th>`).join("") : ""}
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </section>`;
  if (scroll) container.scrollIntoView({ behavior: "smooth", block: "start" });
}

function renderResult(result, container, calculator, { scroll = true } = {}) {
  if (result.error) {
    container.innerHTML = `
      <div class="alert alert-danger error-panel" role="alert">
        <h2 class="h6 alert-heading">Calculation could not be completed</h2>
        <p class="mb-0">${escapeHtml(result.error)}</p>
      </div>`;
    if (scroll) container.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }

  if (result.kind === "range-table") {
    renderRangeTableResult(result, container, calculator, { scroll });
    return;
  }

  const { unitProfile } = loadSettings();
  const unitOverrides = loadUnitOverrides();
  const ordered = result.sections.map((section, index) => ({ ...section, resultIndex: index }))
    .sort((left, right) => left.title === "Input Data" ? 1 : right.title === "Input Data" ? -1 : 0);
  const emptyIndicators = { transonicRegion: null, energyMarkers: [] };
  const trajectoryIndicators = calculator.module === "trajectory"
    ? extractTrajectoryIndicators(result) : emptyIndicators;
  const chartResult = result.chartResult?.error ? result : (result.chartResult || result);
  const chartIndicators = calculator.module === "trajectory"
    ? extractTrajectoryIndicators(chartResult) : emptyIndicators;
  const rangeNotices = ordered.filter(
    (section) => section.title === "Trajectory Range Notice");
  const zeroSolutions = ["solution", "trajectory"].includes(calculator.slug)
    ? [] : ordered.filter((section) => section.title === "Zero Solution");
  const regularSections = ordered.filter(
    (section) => section.title !== "Trajectory Range Notice"
      && section.title !== "Zero Solution");
  const targetSection = result.kind === "solution"
    ? solutionTargetSection(result) : null;
  const engagementZone = result.kind === "solution" && targetSection
    ? renderEngagementZone(
      result.engagementZone, calculator, unitProfile, unitOverrides,
    ) : "";
  const targetUnavailable = result.kind === "solution" && !targetSection;
  const displayedSections = result.kind === "solution"
    ? regularSections.filter((section) => section.title !== "Calculated Table")
    : regularSections;
  const trajectoryChart = renderTrajectoryChart(
    chartResult, calculator, unitProfile, unitOverrides, chartIndicators);
  container.innerHTML = `
    <div class="result-header">
      <h2>Results</h2>
    </div>
    ${renderResultGunStatus(calculator)}
    ${rangeNotices.map((section) => renderResultSection(
      section, calculator, unitProfile, unitOverrides, trajectoryIndicators)).join("")}
    ${zeroSolutions.map((section) => renderResultSection(
      section, calculator, unitProfile, unitOverrides, trajectoryIndicators)).join("")}
    ${trajectoryChart}
    ${targetUnavailable ? `<div class="alert alert-danger solution-target-unavailable"
      data-solution-target-unavailable role="alert">
      <strong>Target solution unavailable.</strong> The trajectory engine ended without an exact sample at the requested target range. Review the trajectory range notice and inputs before using this shot; no firing solution is shown.
    </div>` : ""}
    ${targetSection ? renderResultSection(
    targetSection, calculator, unitProfile, unitOverrides, emptyIndicators,
    { transonicTarget: targetSection.transonicTarget }) : ""}
    ${engagementZone}
    ${displayedSections.map((section) => renderResultSection(
      section, calculator, unitProfile, unitOverrides, trajectoryIndicators)).join("")}`;
  if (scroll) container.scrollIntoView({ behavior: "smooth", block: "start" });
}

function renderAbout() {
  app.innerHTML = `
    <article class="calculator-shell">
      <header class="calculator-heading">
        <div>
          <p class="about-kicker">ABC</p>
          <h1>About Allen Ballistics Classic</h1>
          <p>Allen Ballistics Classic—also called AB Classic or ABC—is an independently developed, modified version of the JBM Ballistics CGI v2.1 calculator source.</p>
        </div>
      </header>
      <div class="about-grid">
        <section class="about-card about-card-wide">
          <i class="bi bi-journal-code"></i>
          <h2>Original source and attribution</h2>
          <p>The original exterior-ballistics package was JBM Ballistics CGI v2.1 by James B. Millard, distributed under the GNU General Public License, version 2 or (at your option) any later version (GPL-2.0-or-later). ABC is a modified work based on that source and is not affiliated with, endorsed by, or an official release of JBM Ballistics. Visit the current <a href="https://jbmballistics.com/" target="_blank" rel="noopener noreferrer">JBM Ballistics website</a>.</p>
          <p>The Allen Ballistics Classic source code can be obtained from <a href="https://git.byte.farm/AWildBeard/abc.git" target="_blank" rel="noopener noreferrer">git.byte.farm/AWildBeard/abc.git</a>.</p>
        </section>
        <section class="about-card">
          <i class="bi bi-cpu"></i>
          <h2>Preserved calculation code</h2>
          <ul>
            <li>Retains the supplied C atmosphere, drag, trajectory, point-blank-range, maximum-distance, and sunrise/sunset calculations.</li>
            <li>Keeps the legacy CGI parameter names and recognizable internal functions as the compatibility boundary.</li>
            <li>Compiles calculator entry points to WebAssembly instead of translating the established formulas into JavaScript.</li>
          </ul>
        </section>
        <section class="about-card">
          <i class="bi bi-graph-up-arrow"></i>
          <h2>Numerical additions and adjustments</h2>
          <ul>
            <li>Adds drag-function conversion for G1, G2, G5, G6, G7, G8, GL, and GI.</li>
            <li>Extends BC-from-velocity and BC-from-time measurements to 2,500 metres.</li>
            <li>Replaces their fixed-step inverse approximations with drag-breakpoint-aware adaptive quadrature and a bounded time solver.</li>
            <li>Extends trajectory tables through 3,000 metres and preserves fractional-yard sampling so converted metric increments and endpoints are calculated at their exact requested bounds.</li>
            <li>Adds book-derived Miller stability, spin-drift, and horizontal/vertical Coriolis corrections to the C trajectory engine.</li>
            <li>Adds fixed, bullet-geometry-estimated, and custom velocity-band BC modes across trajectory, range-table, point-blank-range, and maximum-distance calculations.</li>
            <li>Adds independent numerical, native CGI, and WebAssembly regression tests.</li>
          </ul>
        </section>
        <section class="about-card">
          <i class="bi bi-window-sidebar"></i>
          <h2>Interface and delivery changes</h2>
          <ul>
            <li>Unifies the supplied calculators in a responsive Bootstrap single-page application.</li>
            <li>Adds Imperial, Metric, and Decimal / technical profiles plus per-input and per-result unit controls.</li>
            <li>Adds responsive, collapsible trajectory plots with ten-yard presentation sampling and interpolated transonic and 500/350 ft·lbf retained-energy markers.</li>
            <li>Adds a folder-based loaded-gun library shared by Solution, Temperature Range Table, Point Blank Range, and Maximum Distance, with import/export, exact saved-setting matching, and stale-result warnings when the gun changes. Trajectory retains an isolated setup.</li>
            <li>Adds a single-target Solution with true-north target and wind bearings, latitude-triggered Coriolis calculations, and a densely sampled trajectory plot.</li>
            <li>Adds a dedicated C/WASM Engagement Zone simulation with 1,000 deterministic shots, uncertainty-aware hit probability, and USPSA or custom target plotting.</li>
            <li>Adds a spreadsheet-style temperature Range Table with adjusted-MV drop columns plus low/middle/high samples for configured wind and lateral lead.</li>
            <li>Correlates compatible input and output unit choices across result tables, chart axes, and threshold labels while retaining manual result overrides.</li>
            <li>Uses descriptive public calculator names while retaining the original source layout.</li>
            <li>Converts legacy CGI output into escaped, structured result tables and packages the site for static Cloudflare Worker delivery.</li>
          </ul>
        </section>
        <section class="about-card about-card-wide">
          <i class="bi bi-file-earmark-text"></i>
          <h2>License</h2>
          <p>The original JBM v2.1 source material and the modifications in Allen Ballistics Classic are distributed under the GNU General Public License, version 2 or (at your option) any later version (GPL-2.0-or-later). The software is provided without warranty.</p>
          <a class="btn btn-sm btn-outline-primary" href="/LICENSE.txt" target="_blank" rel="license noopener">Read the full GPLv2 license</a>
        </section>
      </div>
    </article>`;
}

function activeRoute() {
  const match = location.hash.match(/^#\/calculator\/([a-z0-9-]+)$/);
  if (match && getCalculator(match[1])) return { type: "calculator", calculator: getCalculator(match[1]) };
  if (location.hash === "#/gun") return { type: "gun" };
  if (location.hash === "#/about") return { type: "about" };
  return { type: "calculator", calculator: getCalculator("trajectory") };
}

function route() {
  disposeFieldHelpPopovers();
  const current = activeRoute();
  document.querySelectorAll("[data-nav-section]").forEach((link) => {
    const section = current.type === "calculator" ? "calculators" : current.type;
    link.classList.toggle("active", link.dataset.navSection === section);
  });
  document.querySelectorAll("[data-calculator-link]").forEach((link) => {
    link.classList.toggle("active", current.type === "calculator" && link.dataset.calculatorLink === current.calculator.slug);
  });

  if (current.type === "about") renderAbout();
  else if (current.type === "gun") renderGun();
  else renderCalculator(current.calculator);
  window.scrollTo({ top: 0, behavior: "instant" });
}

renderSidebar();
const initialSettings = loadSettings();
applyTheme(initialSettings.theme);
applyUnitProfile(initialSettings.unitProfile);
function handleThemeToggle() {
  applyTheme(document.documentElement.getAttribute("data-bs-theme") === "dark" ? "light" : "dark");
}

function handleUnitProfileChange(event) {
  const current = activeRoute();
  const form = document.querySelector("#calculator-form");
  if (current.type === "calculator" && form) saveForm(current.calculator, form);
  clearUnitOverrides();
  applyUnitProfile(event.target.value);
  route();
}

function handleGunStorage(event) {
  if (event.storageArea && event.storageArea !== localStorage) return;
  if (event.key !== null && event.key !== gunLibrary.storageKey) return;
  gunLibrary.reload();
  route();
}

function handleResize() {
  const form = document.querySelector("#calculator-form, #gun-form");
  if (!form) return;
  const previousRects = captureSectionRects(form, true);
  queueCalculatorBalance(form, { previousRects });
}

const themeToggle = document.querySelector("#theme-toggle");
const unitProfile = document.querySelector("#unit-profile");
themeToggle.addEventListener("click", handleThemeToggle);
unitProfile.addEventListener("change", handleUnitProfileChange);
window.addEventListener("hashchange", route);
window.addEventListener("storage", handleGunStorage);
window.addEventListener("resize", handleResize);
window[APP_CLEANUP_KEY] = () => {
  themeToggle.removeEventListener("click", handleThemeToggle);
  unitProfile.removeEventListener("change", handleUnitProfileChange);
  window.removeEventListener("hashchange", route);
  window.removeEventListener("storage", handleGunStorage);
  window.removeEventListener("resize", handleResize);
  if (calculatorLayoutFrame) {
    const cancelFrame = window.cancelAnimationFrame || window.clearTimeout;
    cancelFrame(calculatorLayoutFrame);
  }
  calculatorLayoutFrame = 0;
  disposeFieldHelpPopovers();
};
route();
